import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { AuthContext } from '../../context/AuthContext';
import { workOrderApi } from '../../api/workOrderApi';
import WorkOrderReportFormModal from './WorkOrderReportFormModal';
import WorkOrderReportDetailModal from './WorkOrderReportDetailModal';
import WorkOrderCommentsTab from './tab/WorkOrderCommentsTab';
import WorkOrderFeedbackTab from './tab/WorkOrderFeedbackTab';
import WorkOrderChecklistTab from './tab/WorkOrderChecklistTab';
import WorkOrderFilesTab from './tab/WorkOrderFilesTab';
import WorkOrderReportsTab from './tab/WorkOrderReportsTab';
import TodoRegisterLayer from '../../components/todo/TodoRegisterLayer';
import FileAttachment from '../../components/FileAttachment/FileAttachment';
import OrgTreeSelect from './WorkOrderOrgTreeSelect';
import { EditorContent } from '@tiptap/react';
import EditorToolbar from '../../components/editor/EditorToolbar';
import tiptapExtensions from '../../components/editor/tiptapExtensions';
import useEditorWithDragDrop from '../../components/editor/useEditorWithDragDrop';
import CommonDatePicker from '../../components/DatePicker/CommonDatePicker';
import WorkOrderOpinionModal from './WorkOrderOpinionModal';
import UserSearchResultModal from './UserSearchResultModal';
import './WorkOrderFormModal.css';

// 중요도 옵션 (WorkOrderFormModal과 100% 동일)
const PRIORITY_OPTIONS = [
  { value: 'urgent', label: '긴급', icon: '🔴', desc: '최우선 처리가 요구되는 긴급 업무입니다.' },
  { value: 'high', label: '높음', icon: '🟠', desc: '일반 업무보다 우선하여 처리해야 하는 업무입니다.' },
  { value: 'normal', label: '보통', icon: '🔵', desc: '일반적인 일정에 따라 수행하는 표준 업무입니다.' },
  { value: 'low', label: '낮음', icon: '⚪', desc: '일정 여유가 있는 보조/참고성 업무입니다.' },
];

const getStatusMeta = (status, deletedAt) => {
  if (deletedAt) {
    return { key: 'deleted', label: '삭제됨' };
  }
  switch (status) {
    case 'progress':
      return { key: 'progress', label: '진행중' };
    case 'review':
      return { key: 'review', label: '결과검토' };
    case 'done':
      return { key: 'done', label: '완료' };
    case 'stop':
      return { key: 'stop', label: '중단' };
    case 'impossible':
      return { key: 'impossible', label: '진행불가' };
    case 'cancel':
    case 'canceled':
      return { key: 'cancel', label: '취소됨' };
    case 'temp':
      return { key: 'temp', label: '임시저장' };
    case 'request':
    default:
      return { key: 'request', label: '대기(미접수)' };
  }
};

// 아바타 색상 팔레트 (PMS 동일)
const AVATAR_COLORS = [
  '#2196F3', '#9C27B0', '#FF9800', '#4CAF50', '#00BCD4',
  '#3F51B5', '#009688', '#FFC107', '#795548', '#607D8B'
];
const getAvatarColorInfo = (name = '') => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  hash = Math.abs(Math.imul(hash, 2654435761));
  const color = AVATAR_COLORS[hash % AVATAR_COLORS.length];
  const isDarkText = ['#FFC107', '#FF9800', '#00BCD4', '#4CAF50'].includes(color);
  return { bg: color, text: isDarkText ? 'dark' : 'light' };
};

// eslint-disable-next-line no-unused-vars
const Avatar = ({ name = '', photo, size = 32 }) => {
  const [imgError, setImgError] = useState(false);
  const info = getAvatarColorInfo(name);
  const initial = name ? name.charAt(0) : '?';
  const showPhoto = photo && !imgError;

  return (
    <div
      className={`d-flex align-items-center justify-content-center text-${info.text} rounded-circle fw-bold shadow-sm flex-shrink-0 overflow-hidden`}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        backgroundColor: showPhoto ? 'transparent' : info.bg,
        fontSize: `${size * 0.42}px`,
        userSelect: 'none'
      }}
    >
      {showPhoto ? (
        <img
          src={photo}
          alt={name}
          onError={() => setImgError(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : initial}
    </div>
  );
};

// eslint-disable-next-line no-unused-vars
const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return dateStr;
  }
};

const formatDateOnly = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr).split('T')[0];
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  } catch {
    return String(dateStr).split('T')[0];
  }
};

function WorkOrderDetailModal({
  open = true,
  workOrderId,
  onClose,
  onStartEdit,
  onDeleted,
  onRefreshList,
  initialTab,
  focusId
}) {
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [workOrder, setWorkOrder] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab || (focusId ? 'feedback' : 'order')); // 'order' | 'reports' | 'comments' | 'files' | 'feedback' | 'checklist'

  // initialTab 또는 focusId 유입 시 탭 자동 전환
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    } else if (focusId) {
      setActiveTab('feedback');
    }
  }, [initialTab, focusId]);

  // 하위 모달 상태
  const [reportFormOpen, setReportFormOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportDetailOpen, setReportDetailOpen] = useState(false);

  // 댓글 입력 상태 (PMS 동일)
  const [newComment, setNewComment] = useState('');
  const [replyTargetId, setReplyTargetId] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);

  // To-Do 등록 레이어 상태
  const [todoSource, setTodoSource] = useState(null);
  const [todoAnchorEl, setTodoAnchorEl] = useState(null);

  // 체크리스트 상태 (PMS 동일)
  const [checklists, setChecklists] = useState([]);
  const [newChecklist, setNewChecklist] = useState('');

  // 파일 폴더 상태 (PMS 동일)
  const [folders, setFolders] = useState([]);

  // 진행 독촉 팝오버 상태
  const [showReminderPopover, setShowReminderPopover] = useState(false);
  const [reminderTargetIds, setReminderTargetIds] = useState([]); // 빈 배열: 전체 대상

  // 업무 관리 드롭업 상태 (지시자 진행중 전용)
  const [showManagementMenu, setShowManagementMenu] = useState(false);
  const managementMenuRef = useRef(null);

  // 업무 관리 드롭업 외부 클릭 시 닫기
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (managementMenuRef.current && !managementMenuRef.current.contains(e.target)) {
        setShowManagementMenu(false);
      }
    };
    if (showManagementMenu) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [showManagementMenu]);

  // 사유 입력 및 의견확인 모달 상태
  const [opinionModalOpen, setOpinionModalOpen] = useState(false);
  const [opinionActionType, setOpinionActionType] = useState('stop'); // 'stop' | 'force_done'
  const [opinionModalMode, setOpinionModalMode] = useState('input'); // 'input' | 'view'

  // ── 인라인 수정 상태 ──
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editPriority, setEditPriority] = useState('normal');
  const [editCompletionType, setEditCompletionType] = useState('individual');
  const [editAssignees, setEditAssignees] = useState([]);
  const [editCcs, setEditCcs] = useState([]);
  const [editAssigneeSearchTerm, setEditAssigneeSearchTerm] = useState('');
  const [editCcSearchTerm, setEditCcSearchTerm] = useState('');
  const [editStartDate, setEditStartDate] = useState(new Date());
  const [editDueDate, setEditDueDate] = useState(new Date());
  const [editorHeight, setEditorHeight] = useState('400px');
  const [editFiles, setEditFiles] = useState([]);
  const [showOrgPopup, setShowOrgPopup] = useState(false);
  const [orgTarget, setOrgTarget] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // 검색결과 다건 선택 모달 상태
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchModalUsers, setSearchModalUsers] = useState([]);
  const [searchModalTarget, setSearchModalTarget] = useState('assignees');

  // Tiptap 에디터 (수정 전용)
  const editor = useEditorWithDragDrop(tiptapExtensions);

  // 수정 모드 진입
  const handleStartEdit = () => {
    if (!workOrder) return;
    setEditTitle(workOrder.title || '');
    setEditPriority(workOrder.priority || 'normal');
    setEditCompletionType(workOrder.completion_type || 'individual');
    setEditStartDate(workOrder.start_date ? new Date(workOrder.start_date) : new Date());
    setEditDueDate(workOrder.due_date ? new Date(workOrder.due_date) : new Date());

    const aList = (workOrder.assignees || []).map(a => {
      const u = a.user || {};
      const pos = u.org?.position || u.position || a.position || '';
      const dept = u.org?.dept_name || u.dept_name || a.department || '';
      const name = u.name || a.name || a.disp_target_name?.split(' ')[0] || '';
      const disp = name ? `${name}${pos ? ' / ' + pos : ''}${dept ? ' (' + dept + ')' : ''}` : (a.disp_target_name || String(u.userid || a.user_id));
      return {
        target_type: 'user',
        target_id: String(u.userid || a.target_id || a.emp_no || a.user_id || a.id),
        target_name: name,
        position: pos,
        dept_name: dept,
        disp_target_name: disp
      };
    });
    setEditAssignees(aList);

    const cList = (workOrder.ccs || []).map(c => {
      const u = c.user || {};
      const pos = u.org?.position || u.position || c.position || '';
      const dept = u.org?.dept_name || u.dept_name || c.department || '';
      const name = u.name || c.name || c.disp_target_name?.split(' ')[0] || '';
      const disp = name ? `${name}${pos ? ' / ' + pos : ''}${dept ? ' (' + dept + ')' : ''}` : (c.disp_target_name || String(u.userid || c.user_id));
      return {
        target_type: 'user',
        target_id: String(u.userid || c.target_id || c.emp_no || c.user_id || c.id),
        target_name: name,
        position: pos,
        dept_name: dept,
        disp_target_name: disp
      };
    });
    setEditCcs(cList);

    // 기존 첨부파일 안전 매핑 (Unknown File / 0 Bytes 방지)
    const rawFiles = (workOrder.files || []).filter(f => !f.report_id);
    setEditFiles(rawFiles.map(f => ({
      ...f,
      id: f.id,
      name: f.file_name || f.originalName || f.name || '첨부파일',
      originalName: f.file_name || f.originalName || f.name || '첨부파일',
      size: Number(f.file_size ?? f.size ?? 0),
      path: f.file_path || f.path || '',
      isNew: false,
      isDeleted: false
    })));

    if (editor && workOrder.content) {
      setTimeout(() => {
        editor.commands.setContent(workOrder.content);
      }, 50);
    }

    setIsEditing(true);
    setActiveTab('order');
  };

  // 수정 취소
  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  // 수정 저장
  const handleSaveEdit = async () => {
    if (!editTitle.trim()) {
      alert('업무 제목을 입력해주세요.');
      return;
    }
    if (editAssignees.length === 0) {
      alert('담당자를 1명 이상 지정해주세요.');
      return;
    }
    const htmlContent = editor ? editor.getHTML() : (workOrder.content || '');
    if (!htmlContent || htmlContent === '<p></p>') {
      alert('요청 내용을 입력해주세요.');
      return;
    }

    try {
      setSavingEdit(true);
      const formData = new FormData();
      formData.append('title', editTitle.trim());
      formData.append('priority', editPriority);
      formData.append('completion_type', editCompletionType);
      formData.append('content', htmlContent);

      const pad = (n) => String(n).padStart(2, '0');
      const formatYMD = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      formData.append('start_date', formatYMD(editStartDate));
      formData.append('due_date', formatYMD(editDueDate));

      const assigneeIds = editAssignees.map(a => a.target_id);
      const ccIds = editCcs.map(c => c.target_id);
      formData.append('assignees', JSON.stringify(assigneeIds));
      formData.append('ccs', JSON.stringify(ccIds));
      formData.append('assigneeIds', JSON.stringify(assigneeIds));
      formData.append('ccIds', JSON.stringify(ccIds));

      editFiles.filter(f => f.isNew && !f.isDeleted).forEach(f => {
        if (f.file) formData.append('files', f.file);
      });

      const deletedFileIds = editFiles
        .filter(f => !f.isNew && f.isDeleted && f.id)
        .map(f => f.id);
      formData.append('deletedFileIds', JSON.stringify(deletedFileIds));

      const res = await workOrderApi.update(workOrderId, formData);
      if (res && res.success === false) {
        alert(res.message || '수정에 실패했습니다.');
        return;
      }
      alert('업무협조 건이 성공적으로 수정되었습니다.');
      setIsEditing(false);
      await loadDetail();
      if (onRefreshList) onRefreshList();
    } catch (err) {
      console.error('업무협조 수정 실패:', err);
      alert(err.response?.data?.message || err.message || '수정 중 오류가 발생했습니다.');
    } finally {
      setSavingEdit(false);
    }
  };

  // 선택된 사용자 목록 등록 처리 (방식 A 상호 배타성 적용, 단 1회 알림 보장)
  const applySelectedUsers = useCallback((itemsToAdd, target) => {
    if (!itemsToAdd || itemsToAdd.length === 0) return;

    const myId = String(user?.id || '');
    const myUserId = String(user?.userid || '');
    const isSelf = (item) => {
      const uId = String(item.target_id || item.userid || item.user_id || item.id || '');
      return Boolean((myId && uId === myId) || (myUserId && uId === myUserId));
    };

    if (target === 'assignees') {
      // 1. 요청자 본인 포함 차단
      const selfItem = itemsToAdd.find(isSelf);
      if (selfItem) {
        alert('요청자 본인은 담당자로 지정할 수 없습니다.');
        setEditAssigneeSearchTerm('');
        return;
      }

      // 2. 이미 참조자에 등록된 사용자 차단
      const ccIds = editCcs.map(c => String(c.target_id || c.userid || ''));
      const alreadyInCc = itemsToAdd.find(n => ccIds.includes(String(n.target_id || n.userid || '')));
      if (alreadyInCc) {
        alert(`이미 참조자로 등록된 사용자(${alreadyInCc.target_name || alreadyInCc.name})는 담당자로 등록할 수 없습니다.`);
        setEditAssigneeSearchTerm('');
        return;
      }

      // 3. 이미 담당자에 등록된 사용자 차단 (단 1회만 경고 후 종료)
      const existingIds = editAssignees.map(p => String(p.target_id || p.userid || ''));
      const toAdd = itemsToAdd.filter(n => !existingIds.includes(String(n.target_id || n.userid || '')));
      if (toAdd.length === 0) {
        alert('이미 담당자로 등록된 사용자입니다.');
        setEditAssigneeSearchTerm('');
        return;
      }

      setEditAssignees(prev => {
        const prevIds = prev.map(p => String(p.target_id || p.userid || ''));
        const reallyNew = toAdd.filter(n => !prevIds.includes(String(n.target_id || n.userid || '')));
        return [...prev, ...reallyNew];
      });
      const toAddIds = toAdd.map(u => String(u.target_id || u.userid || ''));
      setEditCcs(prev => prev.filter(c => !toAddIds.includes(String(c.target_id || c.userid || ''))));
      setEditAssigneeSearchTerm('');
    } else {
      // 1. 요청자 본인 포함 차단
      const selfItem = itemsToAdd.find(isSelf);
      if (selfItem) {
        alert('요청자 본인은 참조자로 지정할 수 없습니다.');
        setEditCcSearchTerm('');
        return;
      }

      // 2. 이미 담당자에 등록된 사용자 차단
      const assigneeIds = editAssignees.map(a => String(a.target_id || a.userid || ''));
      const alreadyInAssignee = itemsToAdd.find(n => assigneeIds.includes(String(n.target_id || n.userid || '')));
      if (alreadyInAssignee) {
        alert(`이미 담당자로 등록된 사용자(${alreadyInAssignee.target_name || alreadyInAssignee.name})는 참조자로 등록할 수 없습니다.`);
        setEditCcSearchTerm('');
        return;
      }

      // 3. 이미 참조자에 등록된 사용자 차단 (단 1회만 경고 후 종료)
      const existingIds = editCcs.map(p => String(p.target_id || p.userid || ''));
      const toAdd = itemsToAdd.filter(n => !existingIds.includes(String(n.target_id || n.userid || '')));
      if (toAdd.length === 0) {
        alert('이미 참조자로 등록된 사용자입니다.');
        setEditCcSearchTerm('');
        return;
      }

      setEditCcs(prev => {
        const prevIds = prev.map(p => String(p.target_id || p.userid || ''));
        const reallyNew = toAdd.filter(n => !prevIds.includes(String(n.target_id || n.userid || '')));
        return [...prev, ...reallyNew];
      });
      setEditCcSearchTerm('');
    }
  }, [user, editAssignees, editCcs]);

  // 인원 검색 (2글자 이상, 단건 자동 추가 / 다건 팝업 선택)
  const handleUserSearch = async (term, target) => {
    const keyword = (term || '').trim();
    if (!keyword) { alert('검색할 이름을 입력해주세요.'); return; }
    if (keyword.length < 2) { alert('검색어를 2자리 이상 입력해 주세요.'); return; }

    const token = user?.token || sessionStorage.getItem('token');
    try {
      const res = await axios.get('/api/org/search', { params: { keyword }, headers: { Authorization: `Bearer ${token}` } });
      const users = res.data.users || [];
      if (users.length === 0) { alert('검색 결과가 존재하지 않습니다.'); return; }

      const newItems = users.map(u => ({
        target_type: 'user',
        target_id: String(u.target_id || u.userid || u.user_id || u.id || u.emp_no || ''),
        target_name: u.name || u.target_name || '',
        position: u.position || '',
        dept_name: u.dept_name || u.department || '',
        disp_target_name: `${u.name || u.target_name || ''}${u.position ? ' / ' + u.position : ''}${u.dept_name || u.department ? ' (' + (u.dept_name || u.department) + ')' : ''}`.trim()
      })).filter(item => item.target_id && item.target_id !== 'undefined');

      if (newItems.length === 0) {
        alert('유효한 사용자를 찾을 수 없습니다.');
        return;
      }

      if (newItems.length === 1) {
        applySelectedUsers(newItems, target);
      } else {
        setSearchModalUsers(newItems);
        setSearchModalTarget(target);
        setShowSearchModal(true);
      }
    } catch (err) {
      console.error('검색 실패:', err);
    }
  };

  // 조직도 콜백
  const handleOrgConfirm = useCallback((selected) => {
    const myId = String(user?.id || '');
    const myUserId = String(user?.userid || '');
    const isSelf = (item) => {
      const tId = String(item.target_id || item.userid || item.user_id || item.id || '');
      return Boolean((myId && tId === myId) || (myUserId && tId === myUserId));
    };

    if (orgTarget === 'assignees') {
      const formatted = selected.map(s => ({
        target_type: 'user',
        target_id: String(s.target_id || s.userid || s.user_id || s.id || ''),
        target_name: s.target_name || s.name || '',
        position: s.position || '',
        dept_name: s.dept_name || s.department || '',
        disp_target_name: s.disp_target_name || `${s.target_name || s.name || ''}${s.position ? ` / ${s.position}` : ''}${s.dept_name || s.department ? ` (${s.dept_name || s.department})` : ''}`
      })).filter(item => item.target_id && item.target_id !== 'undefined');

      // 중복 제거 (target_id 기준)
      const uniqueFormatted = [];
      const seenIds = new Set();
      formatted.forEach(item => {
        if (!seenIds.has(String(item.target_id))) {
          seenIds.add(String(item.target_id));
          uniqueFormatted.push(item);
        }
      });

      const ccIds = editCcs.map(c => String(c.target_id || c.userid || ''));
      const excludedNames = [];

      const validList = uniqueFormatted.filter(item => {
        if (isSelf(item)) {
          excludedNames.push(`${item.target_name || '본인'}(요청자 본인)`);
          return false;
        }
        if (ccIds.includes(String(item.target_id))) {
          excludedNames.push(`${item.target_name}(참조자 중복)`);
          return false;
        }
        return true;
      });

      if (excludedNames.length > 0) {
        alert(`다음 사용자는 담당자로 지정할 수 없어 제외되었습니다:\n• ${excludedNames.join(', ')}`);
      }

      setEditAssignees(validList);
      const ids = validList.map(s => String(s.target_id));
      // 담당자로 지정된 사람이 참조자 목록에 있다면 참조자에서 자동 제거 (방식 A)
      setEditCcs(prev => prev.filter(c => !ids.includes(String(c.target_id || c.userid || ''))));
    } else if (orgTarget === 'ccs') {
      const formatted = selected.map(s => ({
        target_type: 'user',
        target_id: String(s.target_id || s.userid || s.user_id || s.id || ''),
        target_name: s.target_name || s.name || '',
        position: s.position || '',
        dept_name: s.dept_name || s.department || '',
        disp_target_name: s.disp_target_name || `${s.target_name || s.name || ''}${s.position ? ` / ${s.position}` : ''}${s.dept_name || s.department ? ` (${s.dept_name || s.department})` : ''}`
      })).filter(item => item.target_id && item.target_id !== 'undefined');

      // 중복 제거 (target_id 기준)
      const uniqueFormatted = [];
      const seenIds = new Set();
      formatted.forEach(item => {
        if (!seenIds.has(String(item.target_id))) {
          seenIds.add(String(item.target_id));
          uniqueFormatted.push(item);
        }
      });

      const assigneeIds = editAssignees.map(a => String(a.target_id || a.userid || ''));
      const excludedNames = [];

      const validList = uniqueFormatted.filter(item => {
        if (isSelf(item)) {
          excludedNames.push(`${item.target_name || '본인'}(요청자 본인)`);
          return false;
        }
        if (assigneeIds.includes(String(item.target_id))) {
          excludedNames.push(`${item.target_name}(담당자 중복)`);
          return false;
        }
        return true;
      });

      if (excludedNames.length > 0) {
        alert(`다음 사용자는 참조자로 지정할 수 없어 제외되었습니다:\n• ${excludedNames.join(', ')}`);
      }

      setEditCcs(validList);
    }
    setShowOrgPopup(false);
  }, [orgTarget, user, editAssignees, editCcs]);

  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const loadDetail = useCallback(async () => {
    if (!workOrderId) return;
    try {
      setLoading(true);
      const res = await workOrderApi.getDetail(workOrderId);
      if (res.success && res.data) {
        setWorkOrder(res.data);
      } else {
        alert(res.message || '업무 상세 정보를 불러올 수 없습니다.');
        if (onCloseRef.current) onCloseRef.current();
      }
    } catch (err) {
      console.error('[WorkOrderDetailModal] load error:', err);
      alert('업무 정보를 불러오는 중 오류가 발생했습니다.');
      if (onCloseRef.current) onCloseRef.current();
    } finally {
      setLoading(false);
    }
  }, [workOrderId]);

  const loadChecklists = useCallback(async () => {
    if (!workOrderId) return;
    try {
      const res = await workOrderApi.getChecklists(workOrderId);
      if (res.success && res.data) {
        setChecklists(res.data);
      }
    } catch (err) {
      console.error('[loadChecklists] error:', err);
    }
  }, [workOrderId]);

  const loadFolders = useCallback(async () => {
    if (!workOrderId) return;
    try {
      const res = await workOrderApi.getFolders(workOrderId);
      if (res.success && res.data) {
        const rawList = Array.isArray(res.data) ? res.data : [];
        const cleanFolders = rawList.map(f => typeof f === 'string' ? f : (f.folder_name || String(f)));
        setFolders(cleanFolders);
      }
    } catch (err) {
      console.error('[loadFolders] error:', err);
    }
  }, [workOrderId]);

  useEffect(() => {
    if (open && workOrderId) {
      loadDetail();
      loadChecklists();
      loadFolders();
    }
  }, [open, workOrderId, loadDetail, loadChecklists, loadFolders]);


  if (!open) return null;

  // 권한 판별
  const currentUserIdStr = String(user?.id || user?.userid || '');
  const authorIdStr = String(workOrder?.author_id || workOrder?.author?.id || workOrder?.author?.userid || '');
  const isAuthor = currentUserIdStr === authorIdStr;
  const isAdmin = user?.is_admin === true || user?.is_admin === 1 || user?.role === 'admin';
  const canManage = isAuthor || isAdmin;

  const myAssignee = workOrder?.assignees?.find(a =>
    String(a.user_id) === currentUserIdStr ||
    String(a.user?.id) === currentUserIdStr ||
    String(a.user?.userid) === currentUserIdStr
  );
  const isAssignee = !!myAssignee;
  const isCc = !isAuthor && !isAssignee && !isAdmin && Boolean(workOrder?.ccs?.some(c =>
    String(c.user_id) === currentUserIdStr ||
    String(c.user?.id) === currentUserIdStr ||
    String(c.user?.userid) === currentUserIdStr
  ));

  // 업무 접수 액션
  const handleAcceptWorkOrder = async () => {
    if (!window.confirm('이 업무를 접수하고 진행을 시작하시겠습니까?')) return;
    try {
      const res = await workOrderApi.changeAction(workOrderId, { action: 'accept' });
      if (res.success) {
        alert('업무가 성공적으로 접수되어 진행중 상태로 변경되었습니다.');
        loadDetail();
        if (onRefreshList) onRefreshList();
      } else {
        alert(res.message || '접수 처리에 실패했습니다.');
      }
    } catch (err) {
      console.error('[handleAcceptWorkOrder] error:', err);
      alert('업무 접수 중 오류가 발생했습니다.');
    }
  };

  // 액션 버튼 클릭 (사유 모달 분기)
  const handleActionClick = (action) => {
    if (action === 'resume') {
      if (window.confirm('업무를 재개하여 진행중 상태로 복구하시겠습니까?')) {
        executeAction('resume');
      }
      return;
    }
    setOpinionActionType(action);
    setOpinionModalMode('input');
    setOpinionModalOpen(true);
  };

  // 사유 입력 후 액션 실행
  const executeAction = async (action, opinionText = '') => {
    try {
      const res = await workOrderApi.changeAction(workOrderId, { action, opinion: opinionText });
      if (res.success) {
        alert(res.message || '요청이 성공적으로 처리되었습니다.');
        setOpinionModalOpen(false);
        loadDetail();
        if (onRefreshList) onRefreshList();
      } else {
        alert(res.message || '처리에 실패했습니다.');
      }
    } catch (err) {
      console.error('[executeAction] error:', err);
      alert(err.response?.data?.message || err.message || '처리 중 오류가 발생했습니다.');
    }
  };

  // 등록된 의견(사유) 확인 팝업 열기
  const handleOpenOpinionView = () => {
    setOpinionModalMode('view');
    setOpinionModalOpen(true);
  };

  // 지시 삭제 액션 (소프트 삭제)
  const handleDeleteWorkOrder = async () => {
    if (!window.confirm('이 업무협조 건을 삭제(삭제 보관함으로 이동)하시겠습니까?')) return;
    try {
      const res = await workOrderApi.delete(workOrderId);
      if (res.success) {
        alert(res.message || '업무협조 건이 삭제되었습니다.');
        onClose();
        if (onRefreshList) onRefreshList();
      } else {
        alert(res.message || '삭제에 실패했습니다.');
      }
    } catch (err) {
      console.error('[handleDeleteWorkOrder] error:', err);
      alert(err.response?.data?.message || err.message || '삭제 중 오류가 발생했습니다.');
    }
  };

  // 지시 재등록(복구) 액션
  const handleRestoreWorkOrder = async () => {
    if (!window.confirm('이 업무를 다시 진행 상태로 재등록(복구)하시겠습니까?')) return;
    try {
      const res = await workOrderApi.restore(workOrderId);
      if (res.success) {
        alert(res.message || '업무가 정상적으로 재등록되었습니다.');
        loadDetail();
        if (onRefreshList) onRefreshList();
      } else {
        alert(res.message || '재등록에 실패했습니다.');
      }
    } catch (err) {
      console.error('[handleRestoreWorkOrder] error:', err);
      alert(err.response?.data?.message || err.message || '재등록 중 오류가 발생했습니다.');
    }
  };

  // 지시 완전 삭제 액션 (DB 영구 삭제)
  const handleHardDeleteWorkOrder = async () => {
    if (!window.confirm('정말 이 업무를 DB에서 완전히 삭제하시겠습니까?\n삭제 후에는 영구히 복구할 수 없습니다.')) return;
    try {
      const res = await workOrderApi.hardDelete(workOrderId);
      if (res.success) {
        alert(res.message || '업무가 완전히 삭제되었습니다.');
        onClose();
        if (onRefreshList) onRefreshList();
      } else {
        alert(res.message || '완전 삭제에 실패했습니다.');
      }
    } catch (err) {
      console.error('[handleHardDeleteWorkOrder] error:', err);
      alert(err.response?.data?.message || err.message || '완전 삭제 중 오류가 발생했습니다.');
    }
  };

  // 지시 독촉 대상자 발송 액션
  const handleExecuteSendReminder = async () => {
    try {
      const allAssigneeUsers = (workOrder?.assignees || []).map(a => a.user).filter(Boolean);
      let targetNames = [];
      let targetIds = reminderTargetIds;

      if (targetIds.length === 0) {
        targetNames = allAssigneeUsers.map(u => `${u.name} ${u.org?.position || ''}`.trim());
        targetIds = allAssigneeUsers.map(u => u.id);
      } else {
        targetNames = allAssigneeUsers
          .filter(u => targetIds.includes(u.id))
          .map(u => `${u.name} ${u.org?.position || ''}`.trim());
      }

      const res = await workOrderApi.sendReminder(workOrderId, {
        target_user_ids: targetIds,
        target_names: targetNames
      });

      if (res.success) {
        alert(res.message || '독촉 알림이 전송되었습니다.');
        setShowReminderPopover(false);
        loadDetail();
      } else {
        alert(res.message || '독촉 발송에 실패했습니다.');
      }
    } catch (err) {
      console.error('[handleExecuteSendReminder] error:', err);
      alert('독촉 알림 발송 중 오류가 발생했습니다.');
    }
  };

  // 무작위 6자리 난수를 포함한 고유 파일명 생성 로직 (PMS 100% 동일)
  const generateUniqueFile = (origFile) => {
    if (!origFile) return null;
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    const randomDigits = Math.floor(Math.random() * 1000000).toString().padStart(6, '0');
    const uniqueKey = `_${y}${m}${d}F${randomDigits}`;
    const lastDot = origFile.name.lastIndexOf('.');
    let newName;
    if (lastDot !== -1) {
      newName = `${origFile.name.substring(0, lastDot)}${uniqueKey}${origFile.name.substring(lastDot)}`;
    } else {
      newName = `${origFile.name}${uniqueKey}`;
    }
    return new File([origFile], newName, { type: origFile.type });
  };

  // 코멘트/파일 등록 핸들러 (PMS 100% 동일)
  const handleAddComment = async (e) => {
    if (e) e.preventDefault();
    if (!newComment.trim() && selectedFiles.length === 0) {
      return alert('내용 또는 첨부파일을 등록해 주세요.');
    }

    try {
      if (selectedFiles.length > 0) {
        const selectedNames = selectedFiles.map(f => f.name.replace(/_[0-9]{8}F[0-9]{6}(?=\.[^.]+$|$)/g, ''));
        const uniqueSelected = new Set();
        for (const name of selectedNames) {
          if (uniqueSelected.has(name)) {
            return alert(`선택한 파일 목록에 중복된 파일명이 포함되어 있습니다.\n파일명: ${name}`);
          }
          uniqueSelected.add(name);
        }
        const formData = new FormData();
        selectedFiles.forEach(file => {
          const uniqueFile = generateUniqueFile(file);
          formData.append('files', uniqueFile);
        });
        formData.append('source_tab', 'comments');
        formData.append('comment_type', 'comment');
        if (newComment.trim()) {
          formData.append('content', newComment.trim());
        }
        const res = await workOrderApi.addCommentFiles(workOrderId, formData);
        if (res.success || res.ok) {
          setSelectedFiles([]);
          setNewComment('');
          if (fileInputRef.current) fileInputRef.current.value = '';
          loadDetail();
        } else {
          alert(res.message || '파일 업로드 실패');
        }
      } else {
        const res = await workOrderApi.addComment(workOrderId, { content: newComment.trim(), source_tab: 'comments', comment_type: 'comment' });
        if (res.success || res.ok) {
          setNewComment('');
          loadDetail();
        } else {
          alert(res.message || '댓글 등록에 실패했습니다.');
        }
      }
    } catch (err) {
      console.error('[handleAddComment] error:', err);
      alert('댓글 등록 중 오류가 발생했습니다.');
    }
  };

  // 답글 등록 핸들러 (PMS 100% 동일)
  const handleReplySubmit = async (parentId, content, files = [], targetMention = null) => {
    if (!content.trim() && files.length === 0) return;
    try {
      if (files.length > 0) {
        const formData = new FormData();
        files.forEach(file => {
          const uniqueFile = generateUniqueFile(file);
          formData.append('files', uniqueFile);
        });
        formData.append('parent_id', parentId);
        formData.append('source_tab', 'comments');
        formData.append('comment_type', 'comment');
        if (content.trim()) {
          formData.append('content', content.trim());
        }
        if (targetMention) formData.append('target_mention', targetMention);
        const res = await workOrderApi.addCommentFiles(workOrderId, formData);
        if (res.success || res.ok) {
          setReplyTargetId(null);
          setReplyContent('');
          loadDetail();
        } else {
          alert(res.message || '답글 첨부파일 업로드 실패');
        }
      } else {
        const res = await workOrderApi.addComment(workOrderId, { content: content.trim(), parent_id: parentId, target_mention: targetMention, source_tab: 'comments', comment_type: 'comment' });
        if (res.success || res.ok) {
          setReplyTargetId(null);
          setReplyContent('');
          loadDetail();
        } else {
          alert(res.message || '답글 등록 실패');
        }
      }
    } catch (err) {
      console.error('[handleReplySubmit] error:', err);
      alert('답글 등록 중 오류가 발생했습니다.');
    }
  };

  // 코멘트 카드 수정 및 추가 파일 첨부 핸들러 (PMS 100% 동일)
  const handleEditCommentSubmit = async (commentId, content, files = [], originalCreatedAt) => {
    if (!content.trim() && files.length === 0) return false;
    try {
      if (content.trim()) {
        if (commentId) {
          await workOrderApi.updateComment(workOrderId, commentId, { content: content.trim() });
        } else {
          await workOrderApi.addComment(workOrderId, { content: content.trim(), created_at: originalCreatedAt });
        }
      }
      if (files.length > 0) {
        const formData = new FormData();
        files.forEach(file => {
          const uniqueFile = generateUniqueFile(file);
          formData.append('files', uniqueFile);
        });
        if (originalCreatedAt) {
          formData.append('created_at', originalCreatedAt);
        }
        await workOrderApi.addCommentFiles(workOrderId, formData);
      }
      loadDetail();
      return true;
    } catch (e) {
      console.error('[handleEditCommentSubmit] error:', e);
      alert('코멘트 수정 실패');
      return false;
    }
  };

  // 코멘트 삭제 핸들러 (PMS 100% 동일)
  const handleDeleteComment = async (id) => {
    const hasChildren = allComments.some(c => c.parent_id === id);
    const confirmMsg = hasChildren
      ? '이 항목을 삭제하면 하위에 달린 첨부파일도 함께 영구 삭제됩니다.\n정말 삭제하시겠습니까?'
      : '코멘트를 삭제하시겠습니까?';

    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await workOrderApi.deleteComment(workOrderId, id);
      if (res.success || res.ok) {
        loadDetail();
      } else {
        alert(res.message || '삭제 실패');
      }
    } catch (e) {
      console.error('[handleDeleteComment] error:', e);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  // ── [체크리스트 핸들러] ──
  const handleAddChecklist = async (e) => {
    if (e) e.preventDefault();
    if (!newChecklist.trim()) return;
    try {
      const res = await workOrderApi.createChecklist(workOrderId, { content: newChecklist.trim() });
      if (res.success) {
        setNewChecklist('');
        loadChecklists();
      } else {
        alert(res.message || '체크리스트 등록 실패');
      }
    } catch (err) {
      console.error('[handleAddChecklist] error:', err);
      alert('체크리스트 등록 중 오류가 발생했습니다.');
    }
  };

  const handleToggleChecklist = async (checklistId, currentStatus) => {
    try {
      const res = await workOrderApi.updateChecklist(checklistId, { is_completed: !currentStatus });
      if (res.success) {
        setChecklists(prev => prev.map(c => c.id === checklistId ? { ...c, is_completed: !currentStatus } : c));
      } else {
        alert(res.message || '상태 변경 실패');
      }
    } catch (err) {
      console.error('[handleToggleChecklist] error:', err);
    }
  };

  const handleEditChecklist = async (checklistId, content) => {
    try {
      const res = await workOrderApi.updateChecklist(checklistId, { content });
      if (res.success) {
        setChecklists(prev => prev.map(c => c.id === checklistId ? { ...c, content } : c));
      } else {
        alert(res.message || '체크리스트 수정 실패');
      }
    } catch (err) {
      console.error('[handleEditChecklist] error:', err);
    }
  };

  const handleDeleteChecklist = async (checklistId) => {
    if (!window.confirm('체크리스트 항목을 삭제하시겠습니까?')) return;
    try {
      const res = await workOrderApi.deleteChecklist(checklistId);
      if (res.success) {
        setChecklists(prev => prev.filter(c => c.id !== checklistId));
      } else {
        alert(res.message || '체크리스트 삭제 실패');
      }
    } catch (err) {
      console.error('[handleDeleteChecklist] error:', err);
    }
  };

  // ── [폴더 & 파일 핸들러] ──
  const handleCreateFolder = async (folderName) => {
    try {
      const res = await workOrderApi.createFolder(workOrderId, folderName);
      if (res.success) {
        loadFolders();
      } else {
        alert(res.message || '폴더 생성 실패');
      }
    } catch (err) {
      console.error('[handleCreateFolder] error:', err);
      alert('폴더 생성 중 오류가 발생했습니다.');
    }
  };

  const handleRenameFolder = async (oldName, newName) => {
    try {
      const res = await workOrderApi.renameFolder(workOrderId, oldName, newName);
      if (res.success) {
        loadFolders();
        loadDetail();
      } else {
        alert(res.message || '폴더명 변경 실패');
      }
    } catch (err) {
      console.error('[handleRenameFolder] error:', err);
    }
  };

  const handleDeleteFolder = async (folderName, deleteMode) => {
    try {
      const res = await workOrderApi.deleteFolder(workOrderId, folderName);
      if (res.success) {
        loadFolders();
        loadDetail();
      } else {
        alert(res.message || '폴더 삭제 실패');
      }
    } catch (err) {
      console.error('[handleDeleteFolder] error:', err);
    }
  };

  const handleDragMoveFolder = async (commentId, folderName) => {
    try {
      const res = await workOrderApi.updateCommentFolder(commentId, folderName === '' ? null : folderName);
      if (res.success) {
        loadDetail();
      } else {
        alert(res.message || '파일 이동 실패');
      }
    } catch (err) {
      console.error('[handleDragMoveFolder] error:', err);
    }
  };

  // 코멘트와 피드 분리 ('업무지시가 발령되었습니다.' 완전 배제)
  const allComments = workOrder?.comments || [];

  // 체크리스트 통계 카운트
  const completedChecklistCount = checklists.filter(c => c.is_completed).length;
  const totalChecklistCount = checklists.length;

  // 파일 여부 판별 (폴더명, 첨부파일 ID, 파일 객체, 경로, 확장자 등 종합 판별)
  const checkIsFile = (c) => {
    if (!c) return false;
    if (c.folder_name) return true;
    if (c.file || c.file_path || c.file_name || c.attached_file_id) return true;
    if (c.files && c.files.length > 0) return true;
    return c.content && /\.(gif|jpg|jpeg|png|pdf|zip|doc|docx|xls|xlsx|ppt|pptx|txt|hwp|csv|ai|psd)$/i.test(String(c.content).trim());
  };

  // 1. 파일 탭 파일 총 갯수 (WorkOrderFilesTab countDeepFiles와 100% 동일)
  const fileRootComments = allComments.filter(c => (!c.parent_id || c.parent_id === 0) && checkIsFile(c));
  const fileCommentIds = new Set();
  const collectFileChildren = (parentId) => {
    allComments.filter(c => String(c.parent_id) === String(parentId)).forEach(child => {
      fileCommentIds.add(child.id);
      collectFileChildren(child.id);
    });
  };
  fileRootComments.forEach(root => {
    fileCommentIds.add(root.id);
    collectFileChildren(root.id);
  });
  const totalFileCount = fileCommentIds.size;

  // 2. 피드 카운트: 나에게 답변/처리를 요청한 '미해결 요청' 건수 (내가 쓴 글은 제외)
  const currentUserId = user?.id || user?.userid;
  const unresolvedRequestFeedCount = allComments.filter(c => {
    if (c.parent_id || c.comment_type !== 'request' || c.is_resolved) return false;
    if (['업무지시가 발령되었습니다.', '업무협조 요청이 등록되었습니다.', '새로운 업무가 배정되었습니다.'].includes(c.content)) return false;
    // 본인이 직접 작성한 요청은 본인의 답변 대상이 아니므로 제외
    if (String(c.user_id) === String(currentUserId)) return false;

    // 대상자 판별: 지정된 대상자가 없으면 '전체 대상'(나 포함), 대상자가 지정되어 있으면 내가 포함되어야 함
    let targetIds = [];
    if (Array.isArray(c.target_user_id)) targetIds = c.target_user_id;
    else if (typeof c.target_user_id === 'string') {
      try { targetIds = JSON.parse(c.target_user_id); } catch (e) { targetIds = []; }
    }
    if (!targetIds || targetIds.length === 0) return true; // 전체 대상
    return targetIds.some(id => String(id) === String(currentUserId));
  }).length;

  // 3. 코멘트 카운트: WorkOrderCommentsTab의 discussionComments 및 groupComments와 100% 동일하게 집계
  const discussionCommentsForCount = allComments.filter(c => {
    if (['general', 'request', 'feed', 'notice', 'system'].includes(c.comment_type) || c.source_tab === 'feedback') {
      return false;
    }
    if (checkIsFile(c)) return true;
    return c.comment_type === 'comment' || !c.comment_type;
  });
  const rootCommentsOnly = discussionCommentsForCount.filter(c => !c.parent_id || c.parent_id === 0);
  const commentGroups = [];
  if (rootCommentsOnly.length > 0) {
    const sorted = [...rootCommentsOnly].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    let curGroup = { user_id: sorted[0].user_id, created_at: sorted[0].created_at };
    commentGroups.push(curGroup);
    for (let i = 1; i < sorted.length; i++) {
      const c = sorted[i];
      if (c.user_id === curGroup.user_id && c.created_at === curGroup.created_at) {
        // 동일 작성자·동일 시간으로 묶인 같은 카드
      } else {
        curGroup = { user_id: c.user_id, created_at: c.created_at };
        commentGroups.push(curGroup);
      }
    }
  }
  const pureTextCommentCount = commentGroups.length;

  // ── 전체 업무 진행 상태 정밀 판정 (리스트 화면과 100% 동일한 통합 기준) ──
  const getOverallStatusInfo = (wo) => {
    if (!wo) return { key: 'unknown', label: '-', icon: 'fa-info-circle', bg: '#f1f5f9', color: '#64748b', border: '#e2e8f0', badgeClass: 'bg-secondary' };
    if (wo.deleted_at) {
      return { key: 'deleted', label: '삭제됨', icon: 'fa-trash-alt', bg: '#fef2f2', color: '#dc2626', border: '#fecaca', badgeClass: 'bg-danger' };
    }
    if (wo.status === 'canceled' || wo.status === 'cancel') {
      return { key: 'cancel', label: '취소됨', icon: 'fa-ban', bg: '#fff7ed', color: '#ea580c', border: '#fed7aa', badgeClass: 'bg-secondary' };
    }
    if (wo.status === 'stop') {
      return { key: 'stop', label: '중단', icon: 'fa-pause-circle', bg: '#fff7ed', color: '#c2410c', border: '#fed7aa', badgeClass: 'bg-warning text-dark' };
    }
    if (wo.status === 'done' || wo.status === 'completed') {
      return { key: 'done', label: '완료', icon: 'fa-check-circle', bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0', badgeClass: 'bg-success' };
    }
    if (wo.status === 'temp' || wo.status === 'draft') {
      return { key: 'temp', label: '임시저장', icon: 'fa-save', bg: '#f1f5f9', color: '#475569', border: '#e2e8f0', badgeClass: 'bg-secondary' };
    }

    const today = new Date(); today.setHours(0, 0, 0, 0);
    const due = wo.due_date ? new Date(wo.due_date) : null;
    if (due) due.setHours(0, 0, 0, 0);
    const diff = due ? Math.round((due - today) / (1000 * 60 * 60 * 24)) : 0;
    const isDelayed = due && diff < 0;

    if (!isDelayed) {
      if (diff === 0 && !['done', 'completed', 'stop', 'cancel', 'canceled'].includes(wo.status)) {
        return { key: 'today', label: '오늘마감', icon: 'fa-calendar-day', bg: '#fef3c7', color: '#b45309', border: '#fde68a', badgeClass: 'bg-warning text-dark' };
      }
      if (diff > 0 && diff <= 2 && !['done', 'completed', 'stop', 'cancel', 'canceled'].includes(wo.status)) {
        return { key: 'imminent', label: '기한임박', icon: 'fa-hourglass-half', bg: '#fff7ed', color: '#c2410c', border: '#ffedd5', badgeClass: 'bg-warning text-dark' };
      }
      if (wo.status === 'review') {
        return { key: 'review', label: '결과검토', icon: 'fa-file-signature', bg: '#f5f3ff', color: '#6d28d9', border: '#ddd6fe', badgeClass: 'bg-warning text-dark' };
      }
      if (wo.status === 'progress') {
        return { key: 'progress', label: '진행중', icon: 'fa-play-circle', bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe', badgeClass: 'bg-primary' };
      }
      return { key: 'request', label: '대기(미접수)', icon: 'fa-clock', bg: '#fef3c7', color: '#b45309', border: '#fde68a', badgeClass: 'bg-info text-dark' };
    }

    // 마감 경과 시 지연 세부 판정
    const assignees = wo.assignees || [];
    const isJoint = wo.completion_type === 'joint' || wo.completion_type === 'collective';
    if (assignees.length === 0) {
      const label = wo.status === 'request' ? '접수지연' : '진행지연';
      return { key: wo.status === 'request' ? 'delay_request' : 'delay_progress', label, icon: 'fa-exclamation-circle', bg: '#fef2f2', color: '#b91c1c', border: '#fecaca', badgeClass: 'bg-danger' };
    }
    if (isJoint) {
      const hasAccepted = assignees.some(a => a.status !== 'pending');
      const label = hasAccepted ? '진행지연' : '접수지연';
      return { key: hasAccepted ? 'delay_progress' : 'delay_request', label, icon: 'fa-exclamation-circle', bg: '#fef2f2', color: '#b91c1c', border: '#fecaca', badgeClass: 'bg-danger' };
    }

    const pendings = assignees.filter(a => a.status === 'pending');
    const progresses = assignees.filter(a => a.status === 'progress' || a.status === 'review');
    if (pendings.length > 0 && progresses.length > 0) {
      return { key: 'delay_mixed', label: '접수·진행지연', icon: 'fa-exclamation-triangle', bg: '#fef2f2', color: '#b91c1c', border: '#fecaca', badgeClass: 'bg-danger' };
    }
    if (pendings.length > 0 && progresses.length === 0) {
      return { key: 'delay_request', label: '접수지연', icon: 'fa-exclamation-circle', bg: '#fef2f2', color: '#b91c1c', border: '#fecaca', badgeClass: 'bg-danger' };
    }
    return { key: 'delay_progress', label: '진행지연', icon: 'fa-exclamation-circle', bg: '#fef2f2', color: '#b91c1c', border: '#fecaca', badgeClass: 'bg-danger' };
  };

  const getStatusBadge = (wo) => {
    const meta = getOverallStatusInfo(wo);
    return (
      <span className={`badge ${meta.badgeClass} px-2.5 py-1.5 shadow-sm d-inline-flex align-items-center gap-1`} style={{ fontSize: '0.78rem' }}>
        <i className={`fas ${meta.icon}`} />
        {meta.label}
      </span>
    );
  };

  const modalContent = (
    <div className="wo-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="wo-modal-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="wo-modal-content">

          {/* ── 1. 헤더 (업무지시 등록 모달과 동일한 wo-modal-header 높이 규격) ── */}
          <div
            className="wo-modal-header"
            style={{
              background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              padding: '16px 24px'
            }}
          >
            <h5
              className="m-0 fw-bold fs-5 text-white d-flex align-items-center gap-2"
              style={{
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              title={activeTab !== 'order' ? (workOrder?.title || '') : ''}
            >
              <i className={`fas ${isEditing ? 'fa-edit' : (activeTab === 'order' ? 'fa-eye' : 'fa-clipboard-list')} flex-shrink-0`} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {isEditing ? '업무협조 수정' : (activeTab === 'order' ? '업무협조 상세' : (workOrder?.title || '업무협조 상세'))}
              </span>
            </h5>

            <div className="d-flex align-items-center gap-3 flex-shrink-0 ms-3">
              {/* 기존 작성 모달 조회화면과 100% 동일한 지시자 & 지시일시 바 */}
              <div
                className="d-flex align-items-center gap-3"
                style={{
                  fontSize: '0.8rem',
                  color: 'rgba(255,255,255,0.88)',
                  paddingRight: '14px',
                  borderRight: '1px solid rgba(255,255,255,0.25)',
                  marginTop: '1px'
                }}
              >
                <span>
                  <i className="fas fa-user-edit me-1 text-info" />
                  요청자: <strong>
                    {workOrder?.author?.name || workOrder?.author_name || '-'}
                    {workOrder?.author?.org?.position ? ` ${workOrder.author.org.position}` : ''}
                    {workOrder?.author?.org?.dept_name ? ` (${workOrder.author.org.dept_name})` : ''}
                  </strong>
                </span>
                <span>
                  <i className="fas fa-calendar-alt me-1 text-warning" />
                  요청일: <strong>{formatDateOnly(workOrder?.created_at)}</strong>
                </span>
              </div>

              {workOrder && getStatusBadge(workOrder)}
              <button
                type="button"
                className="btn-close btn-close-white ms-1"
                onClick={onClose}
                style={{ boxShadow: 'none' }}
              />
            </div>
          </div>

          {/* ── 2. 메인 탭 네비게이션 (PMS TaskDrawerPanel 100% 동일 nav-tabs, 수정 모드 시 숨김) ── */}
          {!isEditing && (
            <div className="pt-2 px-3 border-bottom-0 d-flex justify-content-between align-items-end" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #0d6efd', flexShrink: 0 }}>
              <ul className="nav nav-tabs border-bottom-0 d-flex flex-nowrap" style={{ gap: '0px', marginBottom: '-1px' }}>
                {[
                  { id: 'order', name: '업무협조', icon: 'fas fa-file-text' },
                  {
                    id: 'reports',
                    name: '처리결과',
                    icon: 'fas fa-file-signature',
                    count: (workOrder?.reports?.length > 0 ? workOrder.reports.length : undefined)
                  },
                  {
                    id: 'comments',
                    name: '코멘트',
                    icon: 'far fa-comments',
                    count: pureTextCommentCount > 0 ? pureTextCommentCount : undefined
                  },
                  {
                    id: 'files',
                    name: '파일',
                    icon: 'far fa-folder',
                    count: totalFileCount
                  },
                  {
                    id: 'feedback',
                    name: '피드',
                    icon: 'fas fa-comments',
                    count: unresolvedRequestFeedCount > 0 ? unresolvedRequestFeedCount : undefined
                  },
                  {
                    id: 'checklist',
                    name: '체크리스트',
                    icon: 'fas fa-tasks',
                    badge: totalChecklistCount > 0 ? `${completedChecklistCount}/${totalChecklistCount}` : undefined
                  }
                ].map((tab, idx) => (
                  <li className="nav-item" key={tab.id}>
                    <button
                      type="button"
                      className={`nav-link d-flex align-items-center justify-content-center ${activeTab === tab.id ? 'fw-bold text-primary' : 'fw-semibold text-dark'}`}
                      style={{
                        borderTop: activeTab === tab.id ? '3px solid #0d6efd' : '1px solid #dee2e6',
                        borderLeft: '1px solid #dee2e6',
                        borderRight: '1px solid #dee2e6',
                        borderBottom: activeTab === tab.id ? '1px solid #ffffff' : '1px solid #0d6efd',
                        backgroundColor: activeTab === tab.id ? '#ffffff' : '#e9ecef',
                        borderRadius: '4px 4px 0 0',
                        marginLeft: idx > 0 ? '-1px' : '0',
                        padding: '10px 18px',
                        fontSize: '0.88rem',
                        position: 'relative',
                        transition: 'background-color 0.2s',
                        zIndex: activeTab === tab.id ? 2 : 1
                      }}
                      onMouseEnter={(e) => { if (activeTab !== tab.id) e.currentTarget.style.backgroundColor = '#d3d9df'; }}
                      onMouseLeave={(e) => { if (activeTab !== tab.id) e.currentTarget.style.backgroundColor = '#e9ecef'; }}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      <i className={`${tab.icon} me-2 ${activeTab === tab.id ? 'text-primary' : 'text-muted'}`} />
                      {tab.name}
                      {tab.count !== undefined && tab.count > 0 && (
                        <span className={`badge ms-2 rounded-pill ${activeTab === tab.id ? 'bg-primary' : 'bg-secondary'}`} style={{ fontSize: '0.65rem' }}>
                          {tab.count}
                        </span>
                      )}
                      {tab.badge !== undefined && (
                        <span className={`badge ms-2 rounded-pill ${activeTab === tab.id ? 'bg-primary' : 'bg-secondary'}`} style={{ fontSize: '0.65rem' }}>
                          {tab.badge}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* ── 3. 모달 본문 ── */}
          <div
            className="wo-modal-body"
            style={{
              backgroundColor: '#f8fafc',
              padding: activeTab === 'order' ? '20px' : '14px',
              overflowY: (activeTab === 'comments' || activeTab === 'feedback') ? 'hidden' : 'auto',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {loading ? (
              <div className="d-flex justify-content-center align-items-center h-100">
                <div className="spinner-border text-primary" />
              </div>
            ) : (
              <>
                {/* ──────────────────────────────────────────────────────── */}
                {/* [탭 1] 업무지시 (조회 모드 및 인라인 수정 모드) */}
                {/* ──────────────────────────────────────────────────────── */}
                {activeTab === 'order' && (
                  isEditing ? (
                    /* ── [수정 모드] 인라인 폼 ── */
                    <div className="row g-3">
                      {/* 1. 업무 제목 */}
                      <div className="col-12">
                        <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                          <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                            <i className="fas fa-file-alt me-1 text-primary" /> 업무 제목 <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={editTitle}
                            onChange={e => setEditTitle(e.target.value)}
                            placeholder="업무 제목을 입력해주세요"
                            maxLength={150}
                            style={{ fontSize: '0.88rem' }}
                          />
                        </div>
                      </div>

                      {/* 2. 중요도 & 진행상황 */}
                      <div className="col-12">
                        <div className="py-2 px-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                          <div className="row g-2 align-items-center">
                            <div className="col-12 col-md-6 border-end-md d-flex align-items-center gap-3">
                              <label className="form-label fw-bold text-secondary mb-0 flex-shrink-0" style={{ fontSize: '0.85rem' }}>
                                <i className="fas fa-flag me-1 text-primary" /> 중요도
                              </label>
                              <div className="d-flex gap-2 flex-wrap">
                                {PRIORITY_OPTIONS.map(opt => (
                                  <button
                                    key={opt.value}
                                    type="button"
                                    className={`wo-priority-btn ${editPriority === opt.value ? 'active ' + opt.value : ''}`}
                                    onClick={() => setEditPriority(opt.value)}
                                  >
                                    <span className="wo-priority-icon">{opt.icon}</span> {opt.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div className="col-12 col-md-6 ps-md-3 d-flex align-items-center gap-3">
                              <label className="form-label fw-bold text-secondary mb-0 flex-shrink-0" style={{ fontSize: '0.85rem' }}>
                                <i className="fas fa-tasks me-1 text-primary" /> 진행상황
                              </label>
                              <div className="d-flex align-items-center gap-2">
                                {(() => {
                                  const meta = getStatusMeta(workOrder.status);
                                  return (
                                    <span
                                      className={`wo-status-badge status-${meta.key}`}
                                      style={{ padding: '2px 10px', fontSize: '0.75rem', borderRadius: '14px', lineHeight: 1.5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                    >
                                      {meta.label} (수정중)
                                    </span>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 3. 담당자 */}
                      <div className="col-12">
                        <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label fw-bold text-secondary mb-0" style={{ fontSize: '0.85rem' }}>
                              <i className="fas fa-users me-1 text-primary" /> 담당자 <span className="text-danger">*</span>
                              {editAssignees.length > 0 && <span className="ms-2 fw-semibold" style={{ fontSize: '0.75rem', color: '#64748b' }}>(총 {editAssignees.length}명)</span>}
                            </label>
                          </div>
                          <div className="card shadow-sm border-0 bg-light bg-opacity-50 mb-0">
                            <div className="card-body p-2">
                              <div className="form-schedule-search-bar mb-2 d-flex align-items-center w-100">
                                <div className="d-flex align-items-center gap-2">
                                  <input
                                    type="text"
                                    autoComplete="off"
                                    value={editAssigneeSearchTerm}
                                    onChange={(e) => setEditAssigneeSearchTerm(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { if (e.nativeEvent.isComposing) return; e.preventDefault(); handleUserSearch(editAssigneeSearchTerm, 'assignees'); } }}
                                    placeholder="이름 검색"
                                    className="form-control d-inline-block w-auto"
                                    style={{ minWidth: '200px', fontSize: '13px', height: '32px' }}
                                  />
                                  <button type="button" className="btn btn-sm" style={{ backgroundColor: '#343a40', color: '#fff', border: 'none', padding: '4px 12px', borderRadius: '4px', whiteSpace: 'nowrap' }} onClick={() => handleUserSearch(editAssigneeSearchTerm, 'assignees')}>검색</button>
                                  <button type="button" className="btn btn-sm" style={{ backgroundColor: 'transparent', border: '1px solid #007bff', color: '#007bff', padding: '4px 12px', borderRadius: '4px', whiteSpace: 'nowrap' }} onClick={() => { setOrgTarget('assignees'); setShowOrgPopup(true); }}>조직도 선택</button>
                                </div>
                                {editAssignees.length > 0 && (
                                  <button type="button" className="btn btn-sm text-nowrap ms-auto" style={{ backgroundColor: '#fff', border: '1px solid #dc3545', color: '#dc3545', padding: '4px 12px', borderRadius: '4px', whiteSpace: 'nowrap' }} onClick={() => setEditAssignees([])}>
                                    <i className="fas fa-trash-alt me-1" />전체삭제
                                  </button>
                                )}
                              </div>

                              {editAssignees.length > 0 ? (
                                <div className="d-flex flex-wrap gap-2 border rounded p-3 bg-white" style={{ minHeight: '48px', maxHeight: '130px', overflowY: 'auto' }}>
                                  {editAssignees.map(a => (
                                    <span key={a.target_id} className="badge d-flex align-items-center gap-2" style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', fontWeight: 600, fontSize: '13px', padding: '5px 10px' }}>
                                      <span>{a.disp_target_name}</span>
                                      <i className="fas fa-times-circle text-primary" style={{ cursor: 'pointer' }} onClick={() => setEditAssignees(prev => prev.filter(x => x.target_id !== a.target_id))} />
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-center py-4 border rounded bg-white text-muted" style={{ fontSize: '0.8rem' }}>
                                  지정된 담당자가 없습니다. 이름 검색 또는 조직도 버튼을 눌러 추가해 주세요.
                                </div>
                              )}
                            </div>
                          </div>

                          {editAssignees.length >= 2 && (
                            <div className="px-1 d-flex align-items-center gap-4" style={{ fontSize: '0.85rem', marginTop: '11px' }}>
                              <span className="text-muted fw-bold">완료 처리 방식 :</span>
                              {[{ value: 'individual', label: '각자 개별 처리 후 완료' }, { value: 'joint', label: '1인 완료 시 전체 완료' }].map(opt => (
                                <label key={opt.value} className="d-flex align-items-center gap-1 mb-0" style={{ cursor: 'pointer' }}>
                                  <input type="radio" name="editCompletionType" value={opt.value}
                                    checked={editCompletionType === opt.value}
                                    onChange={() => setEditCompletionType(opt.value)} />
                                  <span>{opt.label}</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* 4. 참조자 */}
                      <div className="col-12">
                        <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label fw-bold text-secondary mb-0" style={{ fontSize: '0.85rem' }}>
                              <i className="fas fa-share-square me-1 text-info" /> 참조자
                              {editCcs.length > 0 && <span className="ms-2 fw-semibold" style={{ fontSize: '0.75rem', color: '#64748b' }}>(총 {editCcs.length}명)</span>}
                            </label>
                          </div>
                          <div className="card shadow-sm border-0 bg-light bg-opacity-50 mb-0">
                            <div className="card-body p-2">
                              <div className="form-schedule-search-bar mb-2 d-flex align-items-center w-100">
                                <div className="d-flex align-items-center gap-2">
                                  <input
                                    type="text"
                                    autoComplete="off"
                                    value={editCcSearchTerm}
                                    onChange={(e) => setEditCcSearchTerm(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { if (e.nativeEvent.isComposing) return; e.preventDefault(); handleUserSearch(editCcSearchTerm, 'ccs'); } }}
                                    placeholder="이름 검색"
                                    className="form-control d-inline-block w-auto"
                                    style={{ minWidth: '200px', fontSize: '13px', height: '32px' }}
                                  />
                                  <button type="button" className="btn btn-sm" style={{ backgroundColor: '#343a40', color: '#fff', border: 'none', padding: '4px 12px', borderRadius: '4px', whiteSpace: 'nowrap' }} onClick={() => handleUserSearch(editCcSearchTerm, 'ccs')}>검색</button>
                                  <button type="button" className="btn btn-sm" style={{ backgroundColor: 'transparent', border: '1px solid #007bff', color: '#007bff', padding: '4px 12px', borderRadius: '4px', whiteSpace: 'nowrap' }} onClick={() => { setOrgTarget('ccs'); setShowOrgPopup(true); }}>조직도 선택</button>
                                </div>
                                {editCcs.length > 0 && (
                                  <button type="button" className="btn btn-sm text-nowrap ms-auto" style={{ backgroundColor: '#fff', border: '1px solid #dc3545', color: '#dc3545', padding: '4px 12px', borderRadius: '4px', whiteSpace: 'nowrap' }} onClick={() => setEditCcs([])}>
                                    <i className="fas fa-trash-alt me-1" />전체삭제
                                  </button>
                                )}
                              </div>

                              {editCcs.length > 0 ? (
                                <div className="d-flex flex-wrap gap-2 border rounded p-3 bg-white" style={{ minHeight: '48px', maxHeight: '130px', overflowY: 'auto' }}>
                                  {editCcs.map(c => (
                                    <span key={c.target_id} className="badge d-flex align-items-center gap-2" style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', fontWeight: 600, fontSize: '13px', padding: '5px 10px' }}>
                                      <span>{c.disp_target_name}</span>
                                      <i className="fas fa-times-circle text-success" style={{ cursor: 'pointer' }} onClick={() => setEditCcs(prev => prev.filter(x => x.target_id !== c.target_id))} />
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-center py-4 border rounded bg-white text-muted" style={{ fontSize: '0.8rem' }}>
                                  지정된 참조자가 없습니다. 이름 검색 또는 조직도 버튼을 눌러 추가해 주세요.
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 5. 기간 & 진척도 */}
                      <div className="col-12">
                        <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                          {(() => {
                            const assigneesArr = workOrder?.assignees || [];
                            const reportsArr = workOrder?.reports || [];
                            const isDone = ['done', 'completed'].includes(workOrder?.status);
                            const overallProgress = isDone ? 100 : (assigneesArr.length > 0
                              ? Math.round(assigneesArr.reduce((acc, a) => {
                                  if (a.status === 'completed') return acc + 100;
                                  if (a.status === 'submitted') {
                                    const userReps = reportsArr.filter(r => String(r.user_id) === String(a.user_id) && r.approval_status === 'approved');
                                    const maxP = userReps.length > 0 ? Math.max(...userReps.map(r => Number(r.progress_rate) || 0)) : 0;
                                    return acc + maxP;
                                  }
                                  return acc + (Number(a.progress_rate) || 0);
                                }, 0) / assigneesArr.length)
                              : 0);
                            const isComplete = overallProgress === 100;

                            return (
                              <div className="row g-3">
                                {/* 좌측: 기간 (50%) */}
                                <div className="col-12 col-md-6">
                                  <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                                    <i className="fas fa-calendar-alt me-1 text-primary" /> 기간 <span className="text-danger">*</span>
                                  </label>
                                  <div className="d-flex align-items-center gap-2" style={{ minHeight: '38px' }}>
                                    <div className="wo-date-field" style={{ width: '150px', flex: '0 0 150px' }}>
                                      <CommonDatePicker value={editStartDate} onChange={setEditStartDate}
                                        placeholder="시작일 선택" showTime={false} />
                                    </div>
                                    <span className="text-muted fw-bold">~</span>
                                    <div className="wo-date-field" style={{ width: '150px', flex: '0 0 150px' }}>
                                      <CommonDatePicker value={editDueDate} onChange={setEditDueDate}
                                        placeholder="마감일 선택" showTime={false} />
                                    </div>
                                  </div>
                                </div>

                                {/* 우측: 전체 진척도 (50%) */}
                                <div className="col-12 col-md-6">
                                  <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                                    <i className="fas fa-chart-line me-1 text-primary" /> 전체 진척도
                                  </label>
                                  <div className="d-flex align-items-center gap-3 p-2 bg-light rounded" style={{ minHeight: '38px' }}>
                                    <div className="progress flex-grow-1" style={{ height: '8px', borderRadius: '4px', backgroundColor: '#e2e8f0' }}>
                                      <div
                                        className={`progress-bar ${isComplete ? 'bg-success' : 'bg-primary'}`}
                                        style={{ width: `${overallProgress}%`, transition: 'width 0.3s ease' }}
                                      />
                                    </div>
                                    <span className={`fw-bold ${isComplete ? 'text-success' : 'text-primary'}`} style={{ fontSize: '0.85rem', minWidth: '40px', textAlign: 'right' }}>
                                      {overallProgress}%
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>

                      {/* 6. 지시 내용 (에디터) */}
                      <div className="col-12">
                        <div className="p-3 bg-white rounded-3 shadow-sm border d-flex flex-column" style={{ borderColor: '#e2e8f0', height: editorHeight, transition: 'height 0.3s ease' }}>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label fw-bold text-secondary mb-0" style={{ fontSize: '0.85rem' }}>
                              <i className="fas fa-align-left me-1 text-primary" /> 요청 내용 <span className="text-danger">*</span>
                            </label>
                            <div className="d-flex align-items-center gap-2">
                              <span className="text-secondary fw-semibold" style={{ fontSize: '0.8rem' }}>에디터 높이 :</span>
                              <select
                                className="form-select form-select-sm text-secondary"
                                style={{ width: '130px', fontSize: '0.8rem', cursor: 'pointer' }}
                                value={editorHeight}
                                onChange={(e) => setEditorHeight(e.target.value)}
                              >
                                <option value="400px">기본 (400px)</option>
                                <option value="600px">넓게 (600px)</option>
                                <option value="800px">아주 넓게 (800px)</option>
                              </select>
                            </div>
                          </div>
                          <div className="flex-grow-1 bg-light rounded-2 p-1" style={{ minHeight: 0 }}>
                            <div className="border p-1 rounded bg-white h-100 d-flex flex-column">
                              {editor && <EditorToolbar editor={editor} />}
                              <div className="flex-grow-1 overflow-auto bg-white" style={{ minHeight: 0 }}>
                                <EditorContent editor={editor} className="editor-content h-100" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 7. 첨부파일 */}
                      <div className="col-12">
                        <div className="p-3 bg-white rounded-3 shadow-sm border d-flex flex-column" style={{ borderColor: '#e2e8f0' }}>
                          <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                            <i className="fas fa-paperclip me-1 text-primary" /> 비고 (첨부파일)
                          </label>
                          <div className="flex-grow-1 overflow-auto bg-light rounded-2 p-1" style={{ minHeight: 0 }}>
                            <FileAttachment files={editFiles} setFiles={setEditFiles} mode="edit" />
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (() => {
                    /* ── [조회 모드] 기존 읽기 뷰 ── */
                    const getAssigneeStatusMeta = (status) => {
                      switch (status) {
                        case 'complete':
                        case 'completed':
                        case 'done':
                          return { label: '완료', bg: '#10b981', color: '#ffffff', tooltip: '처리결과가 최종 확인완료되어 완료되었습니다.' };
                        case 'review':
                        case 'submitted':
                          return { label: '검토', bg: '#8b5cf6', color: '#ffffff', tooltip: '처리결과가 제출되어 요청자 검토 대기 중입니다.' };
                        case 'reject':
                          return { label: '보완', bg: '#ea580c', color: '#ffffff', tooltip: '처리결과 보완요청 상태입니다.' };
                        case 'progress':
                          return { label: '진행', bg: '#2563eb', color: '#ffffff', tooltip: '업무를 접수하여 정상 진행 중입니다.' };
                        case 'delay':
                          return { label: '지연', bg: '#ef4444', color: '#ffffff', tooltip: '접수 기한을 초과하여 접수가 지연된 상태입니다.' };
                        case 'pending':
                        case 'request':
                        default:
                          return { label: '대기', bg: '#f59e0b', color: '#ffffff', tooltip: '업무를 아직 접수하지 않은 상태입니다 (미접수).' };
                      }
                    };

                    const reportsArr = workOrder.reports || [];
                    const assigneesList = (workOrder.assignees || []).map(a => {
                      const u = a.user || {};
                      const pos = u.org?.position || u.position || a.position || '';
                      const dept = u.org?.dept_name || u.dept_name || a.department || '';
                      const name = u.name || a.name || a.disp_target_name?.split(' ')[0] || '';
                      const disp = name ? `${name}${pos ? ' ' + pos : ''}${dept ? ' (' + dept + ')' : ''}` : (a.disp_target_name || String(a.user_id));

                      // 💡 실제 승인된 결과보고서(최종결과 100%) 및 진척률 교차 검증으로 상태 동기화
                      const userReps = reportsArr.filter(r => String(r.user_id) === String(a.user_id));
                      const isApprovedDone = userReps.some(r => r.approval_status === 'approved' && (r.is_final || Number(r.progress_rate) === 100));

                      let effectiveStatus = a.status || 'request';
                      if (isApprovedDone || ['done', 'completed', 'approved'].includes(a.status)) {
                        effectiveStatus = 'completed';
                      }

                      return {
                        target_id: String(a.user_id || a.target_id || a.emp_no || a.id),
                        disp_target_name: disp,
                        status: effectiveStatus
                      };
                    });

                    const ccsList = (workOrder.ccs || []).map(c => {
                      const u = c.user || {};
                      const pos = u.org?.position || u.position || c.position || '';
                      const dept = u.org?.dept_name || u.dept_name || c.department || '';
                      const name = u.name || c.name || c.disp_target_name?.split(' ')[0] || '';
                      const disp = name ? `${name}${pos ? ' ' + pos : ''}${dept ? ' (' + dept + ')' : ''}` : (c.disp_target_name || String(c.user_id));
                      return {
                        target_id: String(c.user_id || c.target_id || c.emp_no || c.id),
                        disp_target_name: disp
                      };
                    });

                    const formFiles = (workOrder.files || [])
                      .filter(f => !f.report_id)
                      .map((f, idx) => ({
                        ...f,
                        id: f.id || idx,
                        name: f.file_name || f.originalName || f.name || '첨부파일',
                        originalName: f.file_name || f.originalName || f.name || '첨부파일',
                        size: Number(f.file_size || f.size || 0),
                        path: f.file_path || f.path || ''
                      }));

                    return (
                      <div className="row g-3">
                        {/* 1. 업무 제목 */}
                        <div className="col-12">
                          <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                            <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                              <i className="fas fa-file-alt me-1 text-primary" /> 업무 제목
                            </label>
                            <div className="text-dark px-3 py-2 rounded" style={{ fontSize: '0.825rem', fontWeight: 500, backgroundColor: '#f1f5f9' }}>
                              {workOrder.title || '-'}
                            </div>
                          </div>
                        </div>

                        {/* 2. 중요도 & 진행상황 */}
                        <div className="col-12">
                          <div className="py-2 px-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                            <div className="row g-2 align-items-center">
                              <div className="col-12 col-md-6 border-end-md d-flex align-items-center gap-3">
                                <label className="form-label fw-bold text-secondary mb-0 flex-shrink-0" style={{ fontSize: '0.85rem' }}>
                                  <i className="fas fa-flag me-1 text-primary" /> 중요도
                                </label>
                                <div className="d-flex gap-2 flex-wrap">
                                  {PRIORITY_OPTIONS.map(opt => (
                                    <button
                                      key={opt.value}
                                      type="button"
                                      className={`wo-priority-btn ${workOrder.priority === opt.value ? 'active ' + opt.value : ''}`}
                                      style={{ cursor: 'default' }}
                                    >
                                      <span className="wo-priority-icon">{opt.icon}</span> {opt.label}
                                    </button>
                                  ))}
                                </div>
                              </div>

                              <div className="col-12 col-md-6 ps-md-3 d-flex align-items-center gap-3">
                                <label className="form-label fw-bold text-secondary mb-0 flex-shrink-0" style={{ fontSize: '0.85rem' }}>
                                  <i className="fas fa-tasks me-1 text-primary" /> 진행상황
                                </label>
                                <div className="d-flex align-items-center gap-2">
                                  {(() => {
                                    const meta = getOverallStatusInfo(workOrder);
                                    const getDesc = (k) => {
                                      switch (k) {
                                        case 'cancel': return '요청자에 의해 취소된 협조 업무입니다.';
                                        case 'deleted': return '삭제 보관함으로 이동된 업무입니다.';
                                        case 'stop': return '요청자에 의해 일시 중단된 협조 업무입니다.';
                                        case 'done': return '모든 업무 및 처리결과가 완료되었습니다.';
                                        case 'review': return '처리결과가 제출되어 요청자 검토 대기 중입니다.';
                                        case 'progress': return '담당자가 업무를 접수하여 정상 진행 중입니다.';
                                        case 'request': return '담당자가 아직 접수하지 않은 대기 상태입니다.';
                                        case 'delay_request': return '접수 마감 기한을 초과하여 접수가 지연된 상태입니다.';
                                        case 'delay_progress': return '업무 완료 마감 기한을 초과하여 진행이 지연된 상태입니다.';
                                        case 'delay_mixed': return '일부 담당자의 접수 및 진행이 지연된 상태입니다.';
                                        default: return meta.label;
                                      }
                                    };
                                    return (
                                      <span
                                        title={getDesc(meta.key)}
                                        style={{
                                          padding: '1.5px 9.5px',
                                          fontSize: '0.75rem',
                                          fontWeight: 700,
                                          borderRadius: '14px',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          lineHeight: 1.5,
                                          cursor: 'help',
                                          backgroundColor: meta.bg,
                                          color: meta.color,
                                          border: `1.5px solid ${meta.border}`
                                        }}
                                      >
                                        <i className={`fas ${meta.icon} me-1`} style={{ fontSize: '0.75rem' }} />
                                        {meta.label}
                                      </span>
                                    );
                                  })()}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* 3. 담당자 */}
                        <div className="col-12">
                          <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <label className="form-label fw-bold text-secondary mb-0" style={{ fontSize: '0.85rem' }}>
                                <i className="fas fa-users me-1 text-primary" /> 담당자
                                {assigneesList.length > 0 && <span className="ms-2 fw-semibold" style={{ fontSize: '0.75rem', color: '#64748b' }}>(총 {assigneesList.length}명)</span>}
                              </label>
                            </div>
                            <div className="card shadow-sm border-0 bg-light bg-opacity-50 mb-0">
                              <div className="card-body p-2">
                                <div className="d-flex flex-wrap gap-2 align-items-center bg-white p-2 border rounded" style={{ minHeight: '48px', maxHeight: '130px', overflowY: 'auto' }}>
                                  {assigneesList.length > 0 ? assigneesList.map(a => {
                                    const sMeta = getAssigneeStatusMeta(a.status);
                                    return (
                                      <span
                                        key={a.target_id}
                                        className="badge d-inline-flex align-items-center"
                                        style={{
                                          background: '#eff6ff',
                                          color: '#1d4ed8',
                                          border: '1px solid #bfdbfe',
                                          fontWeight: 600,
                                          fontSize: '13px',
                                          padding: '4px 6px 4px 10px',
                                          gap: '6px'
                                        }}
                                      >
                                        <span>{a.disp_target_name}</span>
                                        <span
                                          title={sMeta.tooltip}
                                          style={{
                                            backgroundColor: sMeta.bg,
                                            color: sMeta.color,
                                            fontSize: '11px',
                                            fontWeight: 700,
                                            padding: '1px 6px',
                                            borderRadius: '8px',
                                            cursor: 'help',
                                            lineHeight: 1.4,
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.12)'
                                          }}
                                        >
                                          {sMeta.label}
                                        </span>
                                      </span>
                                    );
                                  }) : <span className="text-muted" style={{ fontSize: '13px' }}>-</span>}
                                </div>
                              </div>
                            </div>

                            {assigneesList.length >= 2 && (
                              <div className="px-1 d-flex align-items-center gap-4" style={{ fontSize: '0.85rem', marginTop: '11px' }}>
                                <span className="text-muted fw-bold">완료 처리 방식 :</span>
                                <span className="p-1 px-2 bg-light rounded text-dark" style={{ display: 'inline-block' }}>
                                  {workOrder.completion_type === 'collective' || workOrder.completion_type === 'joint' ? '1인 완료 시 전체 완료' : '각자 개별 처리 후 완료'}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* 4. 참조자 */}
                        <div className="col-12">
                          <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <label className="form-label fw-bold text-secondary mb-0" style={{ fontSize: '0.85rem' }}>
                                <i className="fas fa-share-square me-1 text-info" /> 참조자
                                {ccsList.length > 0 && <span className="ms-2 fw-semibold" style={{ fontSize: '0.75rem', color: '#64748b' }}>(총 {ccsList.length}명)</span>}
                              </label>
                            </div>
                            <div className="card shadow-sm border-0 bg-light bg-opacity-50 mb-0">
                              <div className="card-body p-2">
                                <div className="d-flex flex-wrap gap-2 align-items-center bg-white p-2 border rounded" style={{ minHeight: '48px', maxHeight: '130px', overflowY: 'auto' }}>
                                  {ccsList.length > 0 ? ccsList.map(c => (
                                    <span key={c.target_id} className="badge" style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', fontWeight: 600, fontSize: '13px', padding: '5px 10px' }}>
                                      {c.disp_target_name}
                                    </span>
                                  )) : <span className="text-muted" style={{ fontSize: '13px' }}>없음</span>}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* 5. 기간 & 진척도 */}
                        {(() => {
                          const assigneesArr = workOrder?.assignees || [];
                          const reportsArr = workOrder?.reports || [];
                          const isDone = ['done', 'completed'].includes(workOrder?.status);
                          const overallProgress = isDone ? 100 : (assigneesArr.length > 0
                            ? Math.round(assigneesArr.reduce((acc, a) => {
                                if (a.status === 'completed') return acc + 100;
                                if (a.status === 'submitted') {
                                  const userReps = reportsArr.filter(r => String(r.user_id) === String(a.user_id) && r.approval_status === 'approved');
                                  const maxP = userReps.length > 0 ? Math.max(...userReps.map(r => Number(r.progress_rate) || 0)) : 0;
                                  return acc + maxP;
                                }
                                return acc + (Number(a.progress_rate) || 0);
                              }, 0) / assigneesArr.length)
                            : 0);
                          const isComplete = overallProgress === 100;

                          return (
                            <div className="col-12">
                              <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                                <div className="row g-3">
                                  {/* 좌측: 기간 (50%) */}
                                  <div className="col-12 col-md-6">
                                    <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                                      <i className="fas fa-calendar-alt me-1 text-primary" /> 기간
                                    </label>
                                    <div className="d-flex align-items-center gap-2">
                                      <div className="p-2 bg-light rounded text-dark text-center" style={{ fontSize: '0.85rem', minHeight: '38px', width: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <span className="text-secondary me-2">시작일 :</span>{formatDateOnly(workOrder.start_date) || '-'}
                                      </div>
                                      <span className="text-muted fw-bold">~</span>
                                      <div className="p-2 bg-light rounded text-dark text-center" style={{ fontSize: '0.85rem', minHeight: '38px', width: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <span className="text-secondary me-2">마감일 :</span>{formatDateOnly(workOrder.due_date) || '-'}
                                      </div>
                                    </div>
                                  </div>

                                  {/* 우측: 전체 진척도 (50%) */}
                                  <div className="col-12 col-md-6">
                                    <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                                      <i className="fas fa-chart-line me-1 text-primary" /> 전체 진척도
                                    </label>
                                    <div className="d-flex align-items-center gap-3 p-2 bg-light rounded" style={{ minHeight: '38px' }}>
                                      <div className="progress flex-grow-1" style={{ height: '8px', borderRadius: '4px', backgroundColor: '#e2e8f0' }}>
                                        <div
                                          className={`progress-bar ${isComplete ? 'bg-success' : 'bg-primary'}`}
                                          style={{ width: `${overallProgress}%`, transition: 'width 0.3s ease' }}
                                        />
                                      </div>
                                      <span className={`fw-bold ${isComplete ? 'text-success' : 'text-primary'}`} style={{ fontSize: '0.85rem', minWidth: '40px', textAlign: 'right' }}>
                                        {overallProgress}%
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                        {/* 6. 지시 내용 */}
                        <div className="col-12">
                          <div className="p-3 bg-white rounded-3 shadow-sm border d-flex flex-column" style={{ borderColor: '#e2e8f0', minHeight: '260px' }}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <label className="form-label fw-bold text-secondary mb-0" style={{ fontSize: '0.85rem' }}>
                                <i className="fas fa-align-left me-1 text-primary" /> 요청 내용
                              </label>
                            </div>
                            <div className="flex-grow-1 bg-light rounded-2 p-1" style={{ minHeight: 0 }}>
                              <div
                                className="bg-white p-3 rounded shadow-sm h-100 overflow-auto ProseMirror wo-content-view"
                                style={{ minHeight: '200px' }}
                                dangerouslySetInnerHTML={{ __html: workOrder.content || '-' }}
                              />
                            </div>
                          </div>
                        </div>

                        {/* 7. 첨부파일 */}
                        {formFiles.length > 0 && (
                          <div className="col-12">
                            <div className="p-3 bg-white rounded-3 shadow-sm border d-flex flex-column" style={{ borderColor: '#e2e8f0' }}>
                              <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                                <i className="fas fa-paperclip me-1 text-primary" /> 비고 (첨부파일)
                              </label>
                              <div className="flex-grow-1 overflow-auto bg-light rounded-2 p-1" style={{ minHeight: 0 }}>
                                <FileAttachment files={formFiles} mode="view" />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()
                )}

                {/* ──────────────────────────────────────────────────────── */}
                {/* [탭 2] 결과보고서 탭 (WorkOrderReportsTab 분리) */}
                {/* ──────────────────────────────────────────────────────── */}
                {activeTab === 'reports' && (
                  <WorkOrderReportsTab
                    workOrder={workOrder}
                    onOpenReportForm={(targetDraft = null) => {
                      setEditingReport(targetDraft);
                      setReportFormOpen(true);
                    }}
                    onOpenReportDetail={(rep) => {
                      setSelectedReport(rep);
                      setReportDetailOpen(true);
                    }}
                    currentUser={user}
                    isCc={isCc}
                    isAssignee={isAssignee}
                  />
                )}

                {/* ──────────────────────────────────────────────────────── */}
                {/* [탭 3] 코멘트 탭 (단독 뷰) */}
                {/* ──────────────────────────────────────────────────────── */}
                {activeTab === 'comments' && (
                  <div className="h-100 d-flex flex-column">
                    <WorkOrderCommentsTab
                      workOrder={workOrder}
                      comments={allComments}
                      newComment={newComment}
                      setNewComment={setNewComment}
                      selectedFiles={selectedFiles}
                      setSelectedFiles={setSelectedFiles}
                      fileInputRef={fileInputRef}
                      handleAddComment={handleAddComment}
                      handleDeleteComment={handleDeleteComment}
                      handleEditCommentSubmit={handleEditCommentSubmit}
                      openFolderModal={null}
                      replyTargetId={replyTargetId}
                      setReplyTargetId={setReplyTargetId}
                      replyContent={replyContent}
                      setReplyContent={setReplyContent}
                      handleReplySubmit={handleReplySubmit}
                      currentUser={user}
                      folders={folders}
                      onRefresh={loadDetail}
                      focusId={focusId}
                      isCc={isCc}
                    />
                  </div>
                )}

                {/* ──────────────────────────────────────────────────────── */}
                {/* [탭 3] 파일 탭 (PMS 100% 동일 폴더 분류 & 드래그 앤 드롭) */}
                {/* ──────────────────────────────────────────────────────── */}
                {activeTab === 'files' && (
                  <div className="h-100">
                    <WorkOrderFilesTab
                      folders={folders}
                      setFolders={setFolders}
                      comments={allComments}
                      handleCreateFolder={handleCreateFolder}
                      handleRenameFolder={handleRenameFolder}
                      handleDeleteFolder={handleDeleteFolder}
                      handleDragMoveFolder={handleDragMoveFolder}
                      handleDeleteComment={async (commentId) => {
                        if (!window.confirm('파일을 삭제하시겠습니까?')) return;
                        try {
                          const res = await workOrderApi.deleteComment(workOrderId, commentId);
                          if (res.success) loadDetail();
                          else alert(res.message || '삭제 실패');
                        } catch (err) {
                          console.error(err);
                        }
                      }}
                      handleReplySubmit={async (parentId, replyText, file) => {
                        try {
                          const formData = new FormData();
                          formData.append('content', replyText || file?.name || '');
                          formData.append('comment_type', 'general');
                          formData.append('source_tab', 'files');
                          formData.append('parent_id', parentId);
                          if (file) formData.append('file', file);
                          const res = await workOrderApi.addComment(workOrderId, formData);
                          if (res.success) loadDetail();
                          else alert(res.message || '등록 실패');
                        } catch (err) {
                          console.error(err);
                        }
                      }}
                      currentUser={user}
                      isAuthor={isAuthor}
                      isAssignee={isAssignee}
                      isCc={isCc}
                      workOrder={workOrder}
                    />
                  </div>
                )}

                {/* ──────────────────────────────────────────────────────── */}
                {/* [탭 5] 피드 탭 (단독 뷰) */}
                {/* ──────────────────────────────────────────────────────── */}
                {activeTab === 'feedback' && (
                  <div className="h-100 d-flex flex-column">
                    <WorkOrderFeedbackTab
                      workOrder={workOrder}
                      comments={allComments}
                      currentUser={user}
                      onRefresh={loadDetail}
                      focusId={focusId}
                      isCc={isCc}
                    />
                  </div>
                )}

                {/* ──────────────────────────────────────────────────────── */}
                {/* [탭 6] 체크리스트 탭 (PMS 100% 동일) */}
                {/* ──────────────────────────────────────────────────────── */}
                {activeTab === 'checklist' && (
                  <div className="h-100">
                    <WorkOrderChecklistTab
                      checklists={checklists}
                      newChecklist={newChecklist}
                      setNewChecklist={setNewChecklist}
                      handleAddChecklist={handleAddChecklist}
                      handleToggleChecklist={handleToggleChecklist}
                      handleEditChecklist={handleEditChecklist}
                      handleDeleteChecklist={handleDeleteChecklist}
                      currentUser={user}
                      isAuthor={isAuthor}
                      isAssignee={isAssignee}
                      isCc={isCc}
                      workOrder={workOrder}
                      focusId={focusId}
                    />
                  </div>
                )}

              </>
            )}
          </div>

          {/* ── 4. 푸터 (탭별·권한별 액션 버튼 동적 전환) ── */}
          <div className="wo-modal-footer d-flex align-items-center justify-content-between w-100">
            {activeTab === 'order' ? (
              isEditing ? (
                <div className="d-flex align-items-center gap-2 ms-auto">
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm px-4 fw-bold"
                    onClick={handleSaveEdit}
                    disabled={savingEdit}
                  >
                    <i className="fas fa-save me-1" />
                    {savingEdit ? '저장 중...' : '수정완료'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm px-3 fw-bold"
                    onClick={handleCancelEdit}
                    disabled={savingEdit}
                  >
                    수정 취소
                  </button>
                </div>
              ) : (
                <>
                  {/* ── 좌측 영역: 지시자/관리자 [업무 관리 ▴] 및 [To-Do 등록] 버튼 ── */}
                  <div className="d-flex align-items-center gap-2">
                    {canManage && (workOrder?.status === 'progress' || workOrder?.status === 'review') && !workOrder?.deleted_at && (
                      <div className="position-relative dropup" ref={managementMenuRef}>
                        <button
                          type="button"
                          className="btn btn-sm px-3 fw-bold d-flex align-items-center shadow-sm"
                          onClick={() => setShowManagementMenu(prev => !prev)}
                          style={{
                            color: '#1d4ed8',
                            backgroundColor: '#eff6ff',
                            borderColor: '#bfdbfe',
                            fontSize: '0.75rem',
                            gap: '8px',
                            transition: 'all 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#dbeafe';
                            e.currentTarget.style.borderColor = '#93c5fd';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#eff6ff';
                            e.currentTarget.style.borderColor = '#bfdbfe';
                          }}
                        >
                          <i className="fas fa-tasks" style={{ fontSize: '0.75rem' }} />
                          <span>업무 관리</span>
                          <i className="fas fa-caret-up" style={{ fontSize: '0.75rem' }} />
                        </button>

                        {showManagementMenu && (
                          <div
                            className="position-absolute shadow-lg text-start"
                            style={{
                              bottom: '100%',
                              left: 0,
                              marginBottom: '10px',
                              width: '165px',
                              zIndex: 1055,
                              backgroundColor: '#f0f7ff',
                              border: '1px solid #bfdbfe',
                              borderRadius: '10px',
                              padding: '6px',
                              boxShadow: '0 12px 28px -4px rgba(0, 0, 0, 0.12), 0 4px 10px rgba(0, 0, 0, 0.04)'
                            }}
                            onClick={() => setShowManagementMenu(false)}
                          >
                            {/* 업무 일시 중단 */}
                            <button
                              type="button"
                              className="btn btn-sm w-100 text-start d-flex align-items-center rounded border-0"
                              style={{
                                padding: '5px 8px',
                                gap: '10px',
                                backgroundColor: 'transparent',
                                transition: 'all 0.15s ease'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0f2fe'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              onClick={() => handleActionClick('stop')}
                            >
                              <div
                                className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                                style={{ width: '24px', height: '24px', backgroundColor: '#fef3c7' }}
                              >
                                <i className="fas fa-pause text-warning" style={{ fontSize: '0.7rem' }} />
                              </div>
                              <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#334155' }}>업무 일시 중단</span>
                            </button>

                            {/* 강제 완료 처리 */}
                            <button
                              type="button"
                              className="btn btn-sm w-100 text-start d-flex align-items-center rounded border-0"
                              style={{
                                padding: '5px 8px',
                                gap: '10px',
                                backgroundColor: 'transparent',
                                transition: 'all 0.15s ease'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0f2fe'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              onClick={() => handleActionClick('force_done')}
                            >
                              <div
                                className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                                style={{ width: '24px', height: '24px', backgroundColor: '#dcfce7' }}
                              >
                                <i className="fas fa-check-double text-success" style={{ fontSize: '0.7rem' }} />
                              </div>
                              <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#16a34a' }}>강제 완료 처리</span>
                            </button>

                            <div style={{ height: '1px', backgroundColor: '#93c5fd', margin: '4px 6px' }} />

                            {/* 업무지시 취소 */}
                            <button
                              type="button"
                              className="btn btn-sm w-100 text-start d-flex align-items-center rounded border-0"
                              style={{
                                padding: '5px 8px',
                                gap: '10px',
                                backgroundColor: 'transparent',
                                transition: 'all 0.15s ease'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0f2fe'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              onClick={() => handleActionClick('cancel')}
                            >
                              <div
                                className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                                style={{ width: '24px', height: '24px', backgroundColor: '#fee2e2' }}
                              >
                                <i className="fas fa-ban text-danger" style={{ fontSize: '0.7rem' }} />
                              </div>
                              <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#dc2626' }}>요청 취소</span>
                            </button>

                            {/* 업무지시 삭제 */}
                            <button
                              type="button"
                              className="btn btn-sm w-100 text-start d-flex align-items-center rounded border-0"
                              style={{
                                padding: '5px 8px',
                                gap: '10px',
                                backgroundColor: 'transparent',
                                transition: 'all 0.15s ease'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0f2fe'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              onClick={handleDeleteWorkOrder}
                            >
                              <div
                                className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                                style={{ width: '24px', height: '24px', backgroundColor: '#fee2e2' }}
                              >
                                <i className="fas fa-trash-alt text-danger" style={{ fontSize: '0.7rem' }} />
                              </div>
                              <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#dc2626' }}>요청삭제</span>
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    {/* [To-Do 등록] 버튼 (업무관리 오른쪽 또는 맨 왼쪽) */}
                    {workOrder && !workOrder?.deleted_at && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm px-3 fw-bold d-flex align-items-center gap-1.5 shadow-sm"
                        style={{ fontSize: '0.75rem', height: '28px' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setTodoAnchorEl({ current: e.currentTarget });
                          setTodoSource({
                            ref_type: 'work_order',
                            ref_table: 'tdx_work_orders',
                            ref_id: workOrder.id,
                            add_title: workOrder.title,
                            add_subtype: 'order',
                            add_id1: workOrder.id,
                            title: `[업무협조] ${workOrder.title}`,
                            memo: `마감일: ${workOrder.due_date ? String(workOrder.due_date).slice(0, 10) : '기한없음'}`
                          });
                        }}
                        title="이 업무협조 건을 나의 To-Do에 등록"
                      >
                        <i className="fas fa-tasks" style={{ fontSize: '0.75rem' }} />
                        <span>To-Do 등록</span>
                      </button>
                    )}
                  </div>

                  {/* ── 우측 영역: 버튼 그룹 ── */}
                  <div className="d-flex align-items-center gap-2 ms-auto">
                    {/* [공통 조건부] 사유(opinions)가 1건 이상 등록되어 있을 때: 의견확인 버튼 (참조자 미노출, 지시자/담당자/관리자만 노출) */}
                    {!isCc && (isAuthor || isAssignee || isAdmin) && workOrder?.opinions && workOrder.opinions.length > 0 && (
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm px-3 fw-bold d-flex align-items-center gap-1.5 shadow-sm"
                        onClick={handleOpenOpinionView}
                      >
                        <i className="fas fa-comment-dots" />
                        <span>의견확인</span>
                      </button>
                    )}

                    {/* [지시자/관리자 취소/삭제 상태일 때]: 복구(재등록) 및 완전삭제 버튼 */}
                    {canManage && (workOrder?.status === 'canceled' || workOrder?.status === 'cancel' || workOrder?.deleted_at) && (
                      <>
                        <button
                          type="button"
                          className="btn btn-outline-info fw-bold btn-sm px-3 shadow-sm"
                          onClick={handleRestoreWorkOrder}
                          title="업무를 정상 진행 상태로 재등록(복구)합니다."
                        >
                          <i className="fas fa-undo me-1" /> 재등록
                        </button>
                        {workOrder?.deleted_at && (
                          <button
                            type="button"
                            className="btn btn-outline-danger fw-bold btn-sm px-3 shadow-sm"
                            onClick={handleHardDeleteWorkOrder}
                            title="이 업무를 DB에서 완전히 삭제합니다."
                          >
                            <i className="fas fa-trash-alt me-1" /> 완전 삭제
                          </button>
                        )}
                      </>
                    )}

                    {/* [지시자/관리자 중단 상태일 때]: 업무 재개 */}
                    {canManage && workOrder?.status === 'stop' && !workOrder?.deleted_at && (
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm px-3 fw-bold shadow-sm"
                        onClick={() => handleActionClick('resume')}
                      >
                        <i className="fas fa-play me-1" />
                        업무 재개
                      </button>
                    )}

                    {/* [지시자/관리자 임시저장 상태일 때]: 지시 삭제 + 지시 수정 */}
                    {canManage && (workOrder?.status === 'temp' || workOrder?.status === 'draft') && !workOrder?.deleted_at && (
                      <>
                        <button
                          type="button"
                          className="btn btn-sm px-3 fw-bold d-flex align-items-center gap-1.5 shadow-sm"
                          style={{
                            color: '#991b1b',
                            backgroundColor: '#fff',
                            borderColor: '#fca5a5',
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            transition: 'all 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#fef2f2';
                            e.currentTarget.style.borderColor = '#ef4444';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#fff';
                            e.currentTarget.style.borderColor = '#fca5a5';
                          }}
                          onClick={handleDeleteWorkOrder}
                          title="이 업무협조 건을 삭제 보관함으로 이동합니다."
                        >
                          <i className="fas fa-trash-alt" />
                          <span>요청삭제</span>
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm px-3 fw-bold"
                          onClick={handleStartEdit}
                        >
                          <i className="fas fa-edit me-1" />
                          요청수정
                        </button>
                      </>
                    )}

                    {/* [지시자/관리자 대기 상태(미접수/접수지연)일 때]: 드롭업 없이 밖으로 4개 그대로 노출 */}
                    {canManage && workOrder?.status === 'request' && !workOrder?.deleted_at && (
                      <>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm px-3 fw-bold d-flex align-items-center gap-1.5 shadow-sm"
                          onClick={() => handleActionClick('cancel')}
                          title="이 업무협조 요청을 취소합니다."
                        >
                          <i className="fas fa-ban" />
                          <span>요청 취소</span>
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm px-3 fw-bold d-flex align-items-center gap-1.5 shadow-sm"
                          style={{
                            color: '#991b1b',
                            backgroundColor: '#fff',
                            borderColor: '#fca5a5',
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            transition: 'all 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#fef2f2';
                            e.currentTarget.style.borderColor = '#ef4444';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#fff';
                            e.currentTarget.style.borderColor = '#fca5a5';
                          }}
                          onClick={handleDeleteWorkOrder}
                          title="이 업무협조 건을 삭제 보관함으로 이동합니다."
                        >
                          <i className="fas fa-trash-alt" />
                          <span>요청삭제</span>
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm px-3 fw-bold"
                          onClick={handleStartEdit}
                        >
                          <i className="fas fa-edit me-1" />
                          요청수정
                        </button>
                      </>
                    )}

                    {/* [지시자/관리자 진행중 상태일 때]: 우측에 [진행 독촉] + [지시 수정](가장 눈에 띄는 Primary 파란색) */}
                    {canManage && (workOrder?.status === 'progress' || workOrder?.status === 'review') && !workOrder?.deleted_at && (
                      <>
                        <div className="position-relative">
                          <button
                            type="button"
                            className="btn btn-outline-warning btn-sm px-3 text-dark fw-bold d-flex align-items-center gap-1.5 shadow-sm"
                            onClick={() => setShowReminderPopover(prev => !prev)}
                            title="담당자들에게 독촉 알림을 전송합니다."
                          >
                            <i className="fas fa-bell text-warning" />
                            <span>진행 독촉</span>
                            <i className={`fas fa-chevron-${showReminderPopover ? 'down' : 'up'}`} style={{ fontSize: '0.7rem' }} />
                          </button>

                          {/* 피드 탭과 100% 동일한 요청 대상자 선택 팝오버 레이어 */}
                          {showReminderPopover && (
                            <div
                              className="position-absolute shadow-lg bg-white rounded-3 p-0 text-start"
                              style={{
                                bottom: '100%',
                                right: 0,
                                marginBottom: '8px',
                                width: '280px',
                                zIndex: 1050,
                                border: '1px solid #c2d7fa',
                                overflow: 'hidden'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="p-2 border-bottom d-flex align-items-center justify-content-between" style={{ backgroundColor: '#e8f0fe' }}>
                                <span className="fw-bold small text-dark">
                                  <i className="fas fa-user-check me-1 text-success" />
                                  독촉 대상자 선택
                                </span>
                                <button
                                  type="button"
                                  className="btn-close"
                                  style={{ fontSize: '0.65rem' }}
                                  onClick={() => setShowReminderPopover(false)}
                                />
                              </div>
                              <div className="p-2" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                {/* 전체 대상 */}
                                <div
                                  className="d-flex align-items-center px-2 py-1.5 mb-1 rounded"
                                  style={{
                                    backgroundColor: reminderTargetIds.length === 0 ? '#e8f0fe' : 'transparent',
                                    border: reminderTargetIds.length === 0 ? '1px solid #d2e3fc' : '1px solid transparent',
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => setReminderTargetIds([])}
                                >
                                  <div className="flex-shrink-0 me-2">
                                    {reminderTargetIds.length === 0 ? (
                                      <div className="rounded-circle d-flex align-items-center justify-content-center bg-primary text-white shadow-sm" style={{ width: '22px', height: '22px' }}>
                                        <i className="fas fa-check" style={{ fontSize: '10px' }} />
                                      </div>
                                    ) : (
                                      <div className="rounded-circle d-flex align-items-center justify-content-center bg-light text-secondary fw-bold shadow-sm border" style={{ width: '22px', height: '22px', fontSize: '10px' }}>
                                        All
                                      </div>
                                    )}
                                  </div>
                                  <span className={`fw-bold small ${reminderTargetIds.length === 0 ? 'text-primary' : 'text-dark'}`}>
                                    ✨ 미완료 대상
                                  </span>
                                </div>

                                {/* 개별 담당자 목록 */}
                                {(workOrder?.assignees || []).map(a => {
                                  const u = a.user || {};
                                  const isSelected = reminderTargetIds.includes(u.id);
                                  const isDone = ['completed', 'done', 'approved'].includes(a.status);
                                  return (
                                    <div
                                      key={a.id}
                                      className="d-flex align-items-center px-2 py-1.5 mb-1 rounded"
                                      style={{
                                        backgroundColor: isSelected ? '#e8f0fe' : 'transparent',
                                        border: isSelected ? '1px solid #d2e3fc' : '1px solid transparent',
                                        cursor: 'pointer'
                                      }}
                                      onClick={() => {
                                        setReminderTargetIds(prev =>
                                          prev.includes(u.id) ? prev.filter(x => x !== u.id) : [...prev, u.id]
                                        );
                                      }}
                                    >
                                      <div className="flex-shrink-0 me-2">
                                        {isSelected ? (
                                          <div className="rounded-circle d-flex align-items-center justify-content-center bg-primary text-white shadow-sm" style={{ width: '22px', height: '22px' }}>
                                            <i className="fas fa-check" style={{ fontSize: '10px' }} />
                                          </div>
                                        ) : (
                                          <Avatar name={u.name} photo={u.org?.photo_path} size={22} />
                                        )}
                                      </div>
                                      <span className={`small ${isSelected ? 'text-primary fw-bold' : 'text-dark'}`}>
                                        {u.name} {u.org?.position || ''}
                                      </span>
                                      {isDone && (
                                        <span className="badge bg-light text-success border border-success-subtle ms-auto" style={{ fontSize: '0.65rem', padding: '2px 5px' }}>
                                          완료
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>

                              <div className="p-2 border-top d-flex justify-content-between align-items-center" style={{ backgroundColor: '#e8f0fe' }}>
                                <span className="small text-muted fw-bold">
                                  선택: <strong className="text-primary">{reminderTargetIds.length === 0 ? '미완료 대상' : `${reminderTargetIds.length}명`}</strong>
                                </span>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary py-0 px-2 fw-bold"
                                  onClick={handleExecuteSendReminder}
                                >
                                  독촉 발송
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* 아웃라인 파란색 지시 수정 버튼 (하단 버튼 전체 아웃라인 스타일 통일) */}
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm px-3 fw-bold shadow-sm d-flex align-items-center gap-1.5"
                          onClick={handleStartEdit}
                        >
                          <i className="fas fa-edit" />
                          <span>요청수정</span>
                        </button>
                      </>
                    )}

                    {/* [담당자] 본인이 미접수(pending) 상태일 때 업무 접수 버튼 노출 (동료가 먼저 접수했더라도 본인 접수 가능) */}
                    {isAssignee && !isAuthor && (myAssignee?.status === 'pending' || (!myAssignee?.status && workOrder?.status === 'request')) && !workOrder?.deleted_at && !['done', 'cancel', 'canceled', 'temp'].includes(workOrder?.status) && (
                      <button
                        type="button"
                        className="btn btn-success btn-sm px-3 fw-bold shadow-sm d-flex align-items-center gap-1.5"
                        onClick={handleAcceptWorkOrder}
                      >
                        <i className="fas fa-rocket" />
                        <span>업무 접수</span>
                      </button>
                    )}

                    {/* 닫기 버튼 */}
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm px-3 fw-bold d-flex align-items-center gap-1.5 shadow-sm"
                      onClick={onClose}
                    >
                      <i className="fas fa-times" />
                      <span>닫기</span>
                    </button>
                  </div>
                </>
              )
            ) : (
              /* [결과보고서, 코멘트/피드, 파일, 체크리스트 탭]일 때: 닫기 버튼만 깔끔하게 유지 */
              <div className="ms-auto">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm px-3 fw-bold d-flex align-items-center gap-1.5 shadow-sm"
                  onClick={onClose}
                >
                  <i className="fas fa-times" />
                  <span>닫기</span>
                </button>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ── 하위 모달: 새 업무보고서 작성 / 이어쓰기 / 재제출 폼 ── */}
      {reportFormOpen && (
        <WorkOrderReportFormModal
          open={reportFormOpen}
          isSubModal={true}
          workOrderId={workOrderId}
          workOrder={workOrder}
          workOrderTitle={workOrder?.title}
          currentProgress={myAssignee?.progress_rate ?? 0}
          report={editingReport}
          reportSeq={
            editingReport?.report_seq ||
            (() => {
              const myId = currentUserIdStr;
              const mySubmittedCount = (workOrder?.reports || []).filter(r => {
                const rId = String(r.user_id || r.reporter?.id || r.reporter?.userid || '');
                return rId === myId && r.approval_status !== 'draft' && r.approval_status !== 'temp';
              }).length;
              return mySubmittedCount + 1;
            })()
          }
          onClose={() => {
            setReportFormOpen(false);
            setEditingReport(null);
          }}
          onSubmitted={() => {
            setReportFormOpen(false);
            setEditingReport(null);
            loadDetail();
            if (onRefreshList) onRefreshList();
          }}
        />
      )}

      {/* ── 하위 모달: 업무보고서 상세 조회 및 승인/반려 ── */}
      {reportDetailOpen && selectedReport && (
        <WorkOrderReportDetailModal
          open={reportDetailOpen}
          workOrderId={workOrderId}
          workOrder={workOrder}
          report={selectedReport}
          isAuthor={isAuthor}
          currentUserId={currentUserIdStr}
          onClose={() => {
            setReportDetailOpen(false);
            setSelectedReport(null);
          }}
          onReviewed={() => {
            loadDetail();
            if (onRefreshList) onRefreshList();
          }}
          onStartEditReport={(rep) => {
            setReportDetailOpen(false);
            setEditingReport(rep);
            setReportFormOpen(true);
          }}
        />
      )}

      {/* ── 사유 입력 및 의견확인 모달 ── */}
      {opinionModalOpen && (
        <WorkOrderOpinionModal
          open={opinionModalOpen}
          mode={opinionModalMode}
          actionType={opinionActionType}
          opinions={workOrder?.opinions || []}
          onClose={() => setOpinionModalOpen(false)}
          onSubmit={(reasonText) => executeAction(opinionActionType, reasonText)}
        />
      )}

      {/* ── To-Do 등록 팝업 레이어 ── */}
      {todoSource && todoAnchorEl && (
        <TodoRegisterLayer
          source={todoSource}
          anchorRef={todoAnchorEl}
          onClose={() => {
            setTodoSource(null);
            setTodoAnchorEl(null);
          }}
          onCreated={() => {
            setTodoSource(null);
            setTodoAnchorEl(null);
          }}
        />
      )}
      {/* ── 조직도 팝업 (Safety 표준 및 업무협조 배타성 적용) ── */}
      {showOrgPopup && (
        <OrgTreeSelect
          selectionMode="multiple"
          targetType="user"
          initialSelected={(orgTarget === 'assignees' ? editAssignees : editCcs).map(a => ({
            target_type: 'user',
            target_id: String(a.target_id || a.userid || ''),
            target_name: a.target_name || a.name || a.disp_target_name?.split('/')[0]?.trim() || '',
            position: a.position || '',
            dept_name: a.dept_name || a.department || '',
            disp_target_name: a.disp_target_name
          }))}
          disabledUserIds={
            orgTarget === 'assignees'
              ? [String(user?.id || ''), String(user?.userid || ''), ...editCcs.map(c => String(c.target_id || c.userid || ''))].filter(Boolean)
              : [String(user?.id || ''), String(user?.userid || ''), ...editAssignees.map(a => String(a.target_id || a.userid || ''))].filter(Boolean)
          }
          onConfirm={handleOrgConfirm}
          onCancel={() => setShowOrgPopup(false)}
        />
      )}

      {/* ── 검색 결과 다건 선택 모달 ── */}
      <UserSearchResultModal
        isOpen={showSearchModal}
        users={searchModalUsers}
        disabledUserIds={
          searchModalTarget === 'assignees'
            ? [String(user?.id || ''), String(user?.userid || ''), ...editCcs.map(c => String(c.target_id || c.userid || '')), ...editAssignees.map(a => String(a.target_id || a.userid || ''))].filter(Boolean)
            : [String(user?.id || ''), String(user?.userid || ''), ...editAssignees.map(a => String(a.target_id || a.userid || '')), ...editCcs.map(c => String(c.target_id || c.userid || ''))].filter(Boolean)
        }
        onConfirm={(selected) => {
          applySelectedUsers(selected, searchModalTarget);
          setShowSearchModal(false);
        }}
        onClose={() => setShowSearchModal(false)}
      />
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}

export default WorkOrderDetailModal;
